import { SearchOutlined } from "@mui/icons-material";
import { Input, Dropdown, Menu, message, Modal, Tabs, Select } from "antd";
import Table from "atoms/Table";
import COLORS from "common/Colors";
import React, { useState, useEffect, useRef } from "react";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import styled, { createGlobalStyle } from "styled-components";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Icon from "atoms/Icon";
import moment from "moment";
import { CALL_API } from "common/API";
import AlertModal from "organisms/AlertModal";
import { filter, isEmpty, isUndefined, uniq } from "lodash";
import { useTranslation } from "react-i18next";
import { callRecruitmentAIAction } from "common/aiChatStream";
import ReactECharts from "echarts-for-react";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RecruitmentChatPanel, { ProgressCard } from "./RecruitmentChatPanel";
import WorkflowAgents from "./WorkflowAgents";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
// PNG, not WebP: the deploy pipeline does not upload .webp — the file 404s to
// index.html on the server, so the image silently renders as nothing. PNG and
// SVG assets ship fine.
import resumeRankerEmptyImg from "assets/resume-ranker-empty.png";

const { TextArea } = Input;
const EMAIL_PURPOSE_OPTIONS = [
  { value: "interview_invitation", label: "Interview invitation" },
  { value: "initial_outreach", label: "Initial outreach" },
  { value: "follow_up", label: "Follow-up" },
];
const EMAIL_TONE_OPTIONS = [
  { value: "professional", label: "Professional" },
  { value: "friendly", label: "Friendly" },
  { value: "formal", label: "Formal" },
];

// Bump this when the shape of the cached rank result changes (e.g. new
// fields added to the AI response) so old caches don't silently serve
// stale/incomplete data instead of re-ranking.
const RANK_CACHE_VERSION = "v4";
function rankCacheKey(jobId) {
  return `rankCache_${RANK_CACHE_VERSION}_${jobId}`;
}

// Signature of the candidate set a ranking was computed against — a candidate
// added, removed, or edited (resume re-uploaded etc.) changes this, which is
// how we know a cached ranking is stale and needs a fresh AI call.
function rankSignature(candidateList, jobId) {
  return filter(
    candidateList,
    (c) => c?.jobId?.toString() === jobId?.toString()
  )
    .map((c) => `${c.candidateId}:${c.updatedAt}`)
    .sort()
    .join(",");
}

function readRankCache(jobId) {
  try {
    const raw = window.localStorage.getItem(rankCacheKey(jobId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writeRankCache(jobId, signature, result) {
  try {
    window.localStorage.setItem(
      rankCacheKey(jobId),
      JSON.stringify({ signature, result })
    );
  } catch (e) {
    // localStorage full/unavailable — cache is a convenience, safe to skip.
  }
}

// Applies a structured filter spec (from the filter_candidates AI action)
// against an already-ranked candidate array. Purely client-side — never
// touches scores, never re-reads resumes, never calls the AI again.
// candidateList is only needed to resolve city/country for a location
// filter — that data lives on the raw candidate record, not the ranked one.
function applyFilterSpec(candidates, spec, candidateList) {
  if (!spec) return candidates;
  return candidates.filter((c) => {
    if (spec.minScore != null && !(typeof c.score === "number" && c.score >= spec.minScore)) return false;
    if (spec.maxScore != null && !(typeof c.score === "number" && c.score <= spec.maxScore)) return false;
    if (spec.minExperience != null && !(typeof c.yearsExperience === "number" && c.yearsExperience >= spec.minExperience)) return false;
    if (spec.maxExperience != null && !(typeof c.yearsExperience === "number" && c.yearsExperience <= spec.maxExperience)) return false;
    if (spec.strongMatchesOnly && !(typeof c.score === "number" && c.score >= 7)) return false;
    if (spec.requiredSkills?.length) {
      const candidateSkills = (c.skills || []).map((s) => s.toLowerCase());
      const hasAllSkills = spec.requiredSkills.every((req) => {
        const reqLower = req.toLowerCase();
        return candidateSkills.some((s) => s.includes(reqLower) || reqLower.includes(s));
      });
      if (!hasAllSkills) return false;
    }
    if (spec.location) {
      const label = (candidateLocationLabel(candidateList, c.candidateId) || "").toLowerCase();
      if (!label.includes(spec.location.toLowerCase())) return false;
    }
    return true;
  });
}

// Everything the Insights dashboard shows is derived from data already on
// hand — the candidate list (for the total count) and the cached AI ranking
// (for skills/score/experience) — no extra API or AI calls.
function computeInsights(candidateList, jobId, cachedResult) {
  const candidates = filter(
    candidateList,
    (c) => c?.jobId?.toString() === jobId?.toString()
  );
  const ranked = cachedResult?.ranked || [];

  const scored = ranked.filter((r) => typeof r.score === "number");
  const strongMatches = scored.filter((r) => r.score >= 7).length;
  const avgScore = scored.length
    ? scored.reduce((sum, r) => sum + r.score, 0) / scored.length
    : null;
  const experienced = ranked.filter((r) => typeof r.yearsExperience === "number");
  const avgExperience = experienced.length
    ? experienced.reduce((sum, r) => sum + r.yearsExperience, 0) / experienced.length
    : null;
  const EXPERIENCE_BUCKETS = [
    { label: "0-1", test: (y) => y <= 1 },
    { label: "1-3", test: (y) => y > 1 && y <= 3 },
    { label: "3-5", test: (y) => y > 3 && y <= 5 },
    { label: "5-8", test: (y) => y > 5 && y <= 8 },
    { label: "8+", test: (y) => y > 8 },
  ];
  const experienceDistribution = EXPERIENCE_BUCKETS.map(({ label, test }) => ({
    label,
    count: experienced.filter((r) => test(r.yearsExperience)).length,
  }));

  const skillCounts = {};
  ranked.forEach((r) => {
    (r.skills || []).forEach((skill) => {
      const key = skill.trim();
      if (!key) return;
      skillCounts[key] = (skillCounts[key] || 0) + 1;
    });
  });
  const topSkills = Object.entries(skillCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([skill, count]) => ({ skill, count }));

  const topCandidates = [...ranked]
    .filter((r) => typeof r.score === "number")
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);

  // City/country live on the raw candidate record, not the ranked entry —
  // cross-reference by candidateId to build a location breakdown for
  // whichever candidates are currently displayed (ranked or filtered).
  const candidateById = {};
  candidates.forEach((c) => { candidateById[c.candidateId] = c; });
  const locationCounts = {};
  ranked.forEach((r) => {
    const full = candidateById[r.candidateId];
    const city = full?.city?.trim();
    const country = full?.country?.trim();
    const label = city && country ? `${city}, ${country}` : city || country;
    if (!label) return;
    locationCounts[label] = (locationCounts[label] || 0) + 1;
  });
  // Capped to 5 (not 8) to match Experience Distribution's fixed 5 buckets —
  // the two donuts are paired side by side and need equal legend length to
  // stay visually balanced regardless of how many distinct locations exist.
  const topLocations = Object.entries(locationCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([label, count]) => ({ label, count }));
  const locatedCount = Object.values(locationCounts).reduce((sum, c) => sum + c, 0);

  const shortlisted = strongMatches;
  const notShortlisted = Math.max(0, ranked.length - shortlisted);

  const avgOf = (key) => {
    const vals = ranked.filter((r) => typeof r[key] === "number").map((r) => r[key]);
    return vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : null;
  };
  const matchAverages = {
    skillMatch: avgOf("skillMatch"),
    experienceMatch: avgOf("experienceMatch"),
    educationMatch: avgOf("educationMatch"),
    projectMatch: avgOf("projectMatch"),
  };

  // Short missing-skill tags from the AI (analogous to `skills`, not the
  // prose `gaps` bullets) — deduped, most-repeated first.
  const gapCounts = {};
  ranked.forEach((r) => {
    (r.missingSkills || []).forEach((skill) => {
      const key = skill.trim();
      if (!key) return;
      gapCounts[key] = (gapCounts[key] || 0) + 1;
    });
  });
  const commonGaps = Object.entries(gapCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([gap]) => gap);

  const qualityTrendSource = [...scored].sort((a, b) => b.score - a.score).slice(0, 12);
  const qualityTrend = [];
  for (let i = 0; i < qualityTrendSource.length; i += 3) {
    const chunk = qualityTrendSource.slice(i, i + 3);
    if (!chunk.length) continue;
    qualityTrend.push({
      label: `Top ${i + 1}-${i + chunk.length}`,
      avgScore100: Math.round((chunk.reduce((s, r) => s + r.score, 0) / chunk.length) * 10),
    });
  }

  return {
    strongMatches,
    experienceDistribution,
    avgScore,
    avgExperience,
    topSkills,
    topLocations,
    locatedCount,
    topCandidates,
    shortlisted,
    notShortlisted,
    matchAverages,
    commonGaps,
    qualityTrend,
  };
}

// Donut chart for Experience distribution — the buckets are mutually
// exclusive and sum to every ranked candidate, so this genuinely
// represents parts of a whole (unlike Top skills, where one candidate
// can count toward several bars).
// Shared categorical palette (blue/green/purple/orange) used across every
// Insights chart/graph/tag so they all read as one consistent visual system.
const CHART_PALETTE = [COLORS.PRIMARY, COLORS.SUCCESS, "#7B5FE0", "#F5A623"];
const EXPERIENCE_DONUT_SHADES = [...CHART_PALETTE, "#EC4899"];

// Centered "<total> candidates" label — shared by every donut so they all
// read the same way (ECharts pies have no built-in center label).
function donutCenterGraphic(total) {
  return {
    elements: [{
      type: "text",
      left: "center",
      top: "middle",
      style: {
        text: `{count|${total}}\n{label|candidates}`,
        rich: {
          count: { fontSize: 20, fontWeight: 700, fill: COLORS.BLACK, fontFamily: "inherit", lineHeight: 22, align: "center" },
          label: { fontSize: 9.5, fill: COLORS.TEXT.PRIMARY, fontFamily: "inherit", lineHeight: 14, align: "center" },
        },
        align: "center",
        verticalAlign: "middle",
      },
    }],
  };
}

// No built-in ECharts legend — at this card width its text truncates
// unreadably ("0-1 yrs (1" with the count cut off). A real HTML legend
// (rendered by the caller) never truncates.
function ExperienceDistributionChart({ experienceDistribution }) {
  const withCounts = (experienceDistribution || []).filter((b) => b.count > 0);
  if (!withCounts.length) return null;
  const total = withCounts.reduce((sum, b) => sum + b.count, 0);
  const option = {
    tooltip: { trigger: "item" },
    graphic: donutCenterGraphic(total),
    series: [{
      type: "pie",
      radius: ["44%", "72%"],
      center: ["50%", "50%"],
      avoidLabelOverlap: true,
      itemStyle: { borderColor: "#fff", borderWidth: 2 },
      label: { show: false },
      data: withCounts.map((b, i) => ({
        name: `${b.label} yrs (${b.count})`,
        value: b.count,
        itemStyle: { color: EXPERIENCE_DONUT_SHADES[i % EXPERIENCE_DONUT_SHADES.length] },
      })),
    }],
  };
  return (
    <ReactECharts
      option={option}
      style={{ height: 200, width: 200, flexShrink: 0 }}
      opts={{ renderer: "svg" }}
      notMerge
      lazyUpdate
    />
  );
}

// Donut for Location Analysis — same visual language as the experience
// donut above, colored from the same shared categorical palette.
function LocationDistributionChart({ topLocations, locatedCount }) {
  if (!topLocations || !topLocations.length) return null;
  const option = {
    tooltip: { trigger: "item" },
    graphic: donutCenterGraphic(locatedCount),
    series: [{
      type: "pie",
      radius: ["44%", "72%"],
      center: ["50%", "50%"],
      avoidLabelOverlap: true,
      itemStyle: { borderColor: "#fff", borderWidth: 2 },
      label: { show: false },
      data: topLocations.map((l, i) => ({
        name: `${l.label} (${l.count})`,
        value: l.count,
        itemStyle: { color: CHART_PALETTE[i % CHART_PALETTE.length] },
      })),
    }],
  };
  return (
    <ReactECharts
      option={option}
      style={{ height: 200, width: 200, flexShrink: 0 }}
      opts={{ renderer: "svg" }}
      notMerge
      lazyUpdate
    />
  );
}

// Bar chart of the 4 average match percentages (Skill/Experience/Education/
// Project) across the currently displayed candidates.
function MatchDistributionChart({ matchAverages }) {
  const items = [
    { label: "Skill Match", shortLabel: "Skill", value: matchAverages.skillMatch },
    { label: "Experience Match", shortLabel: "Experience", value: matchAverages.experienceMatch },
    { label: "Education Match", shortLabel: "Education", value: matchAverages.educationMatch },
    { label: "Project Match", shortLabel: "Project", value: matchAverages.projectMatch },
  ].filter((i) => typeof i.value === "number");
  if (!items.length) return null;
  const option = {
    grid: { left: 36, right: 16, top: 24, bottom: 28 },
    xAxis: {
      type: "category",
      data: items.map((i) => i.shortLabel),
      axisLine: { lineStyle: { color: "#ECEFF7" } },
      axisTick: { show: false },
      axisLabel: { fontSize: 10.5, fontFamily: "inherit", color: COLORS.TEXT.PRIMARY, interval: 0 },
    },
    yAxis: { type: "value", max: 100, axisLabel: { fontSize: 10, formatter: "{value}%" }, splitLine: { lineStyle: { color: "#F2F4FA" } } },
    series: [{
      type: "bar",
      data: items.map((i, idx) => ({
        value: i.value,
        itemStyle: { color: CHART_PALETTE[idx % CHART_PALETTE.length], borderRadius: [6, 6, 0, 0] },
      })),
      barWidth: 34,
      label: { show: true, position: "top", formatter: "{c}%", fontSize: 11, fontFamily: "inherit", color: COLORS.BLACK },
    }],
    tooltip: { trigger: "item", formatter: "{b}: {c}%" },
  };
  return <ReactECharts option={option} style={{ height: 220, width: "100%" }} opts={{ renderer: "svg" }} notMerge lazyUpdate />;
}

// Line chart of average score (/100) across rank buckets (Top 1-3, 4-6, ...).
function QualityTrendChart({ qualityTrend }) {
  if (!qualityTrend || qualityTrend.length < 2) return null;
  const option = {
    grid: { left: 36, right: 16, top: 20, bottom: 28 },
    xAxis: {
      type: "category",
      data: qualityTrend.map((q) => q.label),
      axisLine: { lineStyle: { color: "#ECEFF7" } },
      axisTick: { show: false },
      axisLabel: { fontSize: 10.5, fontFamily: "inherit", color: COLORS.TEXT.PRIMARY },
    },
    yAxis: { type: "value", min: 0, max: 100, axisLabel: { fontSize: 10 }, splitLine: { lineStyle: { color: "#F2F4FA" } } },
    series: [{
      type: "line",
      data: qualityTrend.map((q) => q.avgScore100),
      smooth: true,
      symbolSize: 7,
      lineStyle: { color: COLORS.PRIMARY, width: 2.5 },
      itemStyle: { color: COLORS.PRIMARY },
      areaStyle: { color: "rgba(45, 83, 218, 0.08)" },
    }],
    tooltip: { trigger: "item" },
  };
  return <ReactECharts option={option} style={{ height: 200, width: "100%" }} opts={{ renderer: "svg" }} notMerge lazyUpdate />;
}

function scoreColor(score) {
  if (score === null || score === undefined) return COLORS.TEXT.PRIMARY;
  if (score >= 8) return COLORS.SUCCESS;
  if (score >= 6) return COLORS.PRIMARY;
  if (score >= 4) return COLORS.WARNING;
  return COLORS.ERROR;
}

// Display-only — the underlying score stays 0-10 everywhere (scoreColor
// thresholds, filter comparisons, sorting all keep working unchanged).
// This just formats it as a /100 value for the UI.
function formatScore100(score) {
  return typeof score === "number" ? Math.round(score * 10) : "—";
}

function matchBarColor(pct) {
  if (typeof pct !== "number") return COLORS.TEXT.PRIMARY;
  if (pct >= 80) return COLORS.SUCCESS;
  if (pct >= 60) return COLORS.PRIMARY;
  if (pct >= 40) return COLORS.WARNING;
  return COLORS.ERROR;
}

function candidateLocationLabel(candidateList, candidateId) {
  const full = filter(candidateList, (c) => c.candidateId === candidateId)[0];
  const city = full?.city?.trim();
  const country = full?.country?.trim();
  return city && country ? `${city}, ${country}` : city || country || null;
}

const RankAiButton = styled(ButtonOutlined)`
  border-radius: 8px;
  padding: 7px 18px;
  font-weight: 500;
`;

const AiTitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.BLACK};
`;
const SectionOneHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  padding: 12px 20px;
  border-bottom: 1px solid #ECEFF7;
`;
const SectionOneHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;
const MenuItemLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;
const SectionOneMenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: ${COLORS.TEXT.PRIMARY};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #F0F2F9;
    color: ${COLORS.BLACK};
  }
`;
const SectionOneCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: ${COLORS.TEXT.PRIMARY};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #F0F2F9;
    color: ${COLORS.BLACK};
  }
  svg { font-size: 17px; }
`;
const AiBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: #E9EDFC;
  color: ${COLORS.PRIMARY};
  font-size: 13px;
  flex-shrink: 0;
`;
const CommandInput = styled(Input)`
  flex: 1;
  border-radius: 6px;
  border: 1px solid #E5E9F7;

  &:hover, &:focus {
    border-color: ${COLORS.PRIMARY};
  }
`;
const ChatErrorText = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${COLORS.ERROR};
`;
const EmailFieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;
const EmailFieldLabel = styled.div`
  width: 70px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.TEXT.PRIMARY};
`;
const EmailPreview = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ECEFF7;
`;
const EmailPreviewNote = styled.div`
  margin-top: 8px;
  font-size: 11px;
  color: ${COLORS.TEXT.PRIMARY};
`;

// Section 1's Email Report card — deliberately its own set of styled
// components (not the shared EmailFieldRow/FilterNavButton used by the
// older single-candidate composer) so restyling this doesn't touch that.
// Compact single-screen "Review Emails" modal (header + nav + scrollable
// fields/body + sticky footer) — no tabs, no nested cards, fits ~80vh.
const EmailReviewShell = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 80vh;
`;
const EmailReviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid #ECEFF7;
  flex-shrink: 0;
`;
const EmailReviewHeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;
const EmailReviewHeaderIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.SIDEBAR};
  color: ${COLORS.PRIMARY};
  svg { font-size: 19px; }
`;
const EmailReviewHeaderTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.BLACK};
`;
const EmailReviewHeaderSubtitle = styled.div`
  font-size: 12.5px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-top: 2px;
`;
const EmailReviewCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${COLORS.TEXT.PRIMARY};
  cursor: pointer;
  flex-shrink: 0;

  &:hover { background: ${COLORS.SIDEBAR}; color: ${COLORS.BLACK}; }
  svg { font-size: 16px; }
`;
const EmailReviewNavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 22px;
  border-bottom: 1px solid #ECEFF7;
  flex-shrink: 0;
`;
const EmailReviewNavBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 1px solid #D8E0FA;
  background: #fff;
  color: ${COLORS.PRIMARY};
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s ease;

  &:hover:not(:disabled) { background: ${COLORS.SIDEBAR}; }
  &:disabled { color: #C0C6DA; border-color: #ECEFF7; cursor: default; }
  svg { font-size: 15px; }
`;
const EmailReviewCandidate = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;
const EmailReviewAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #E9E4FB;
  color: #6A4FD6;
  font-size: 12.5px;
  font-weight: 700;
`;
const EmailReviewCandidateText = styled.div`
  min-width: 0;
`;
const EmailReviewCandidateName = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: ${COLORS.BLACK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const EmailReviewCandidateMeta = styled.div`
  font-size: 11.5px;
  color: ${COLORS.TEXT.PRIMARY};
`;
const EmailReviewStatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
  white-space: nowrap;
  background: ${(props) => (props.$tone === "sending" ? "#FFF4E5" : "#E7F7EE")};
  color: ${(props) => (props.$tone === "sending" ? COLORS.WARNING : COLORS.SUCCESS)};
  svg { font-size: 13px; }
`;
const EmailReviewScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 22px 20px;
`;
const EmailReviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px solid #F1F3F9;
`;
const EmailReviewRowIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.SIDEBAR};
  color: ${COLORS.PRIMARY};
  svg { font-size: 14px; }
`;
const EmailReviewRowLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.TEXT.PRIMARY};
  width: 56px;
  flex-shrink: 0;
`;
const EmailReviewRowInput = styled(Input)`
  flex: 1;
  min-width: 0;
  border: 1px solid transparent;
  background: transparent;
  padding: 4px 6px;
  font-size: 13.5px;
  color: ${COLORS.BLACK};
  box-shadow: none;

  &:hover, &:focus { background: ${COLORS.SIDEBAR}; border-radius: 6px; box-shadow: none; }
  &:disabled { color: ${COLORS.BLACK}; }
`;
const EmailReviewBodyBox = styled(TextArea)`
  margin-top: 10px;
  border-radius: 12px;
  border: 1px solid #ECEFF7;
  background: #F8F9FC;
  padding: 16px 18px;
  font-size: 13.5px;
  line-height: 1.65;
  color: ${COLORS.BLACK};
  box-shadow: none;
  resize: none;

  &:hover, &:focus { background: #F8F9FC; border-color: ${COLORS.SIDEBAR}; box-shadow: none; }
  &:disabled { color: ${COLORS.BLACK}; }
`;
const EmailReviewFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid #ECEFF7;
  background: #fff;
  flex-shrink: 0;
`;
const EmailReviewCancelButton = styled(ButtonOutlined)`
  border-radius: 999px;
  padding: 9px 20px;
  font-weight: 600;
  font-size: 13.5px;
`;
const EmailSendButton = styled(ButtonFilled)`
  border-radius: 999px;
  padding: 10px 22px;
  font-weight: 600;
  font-size: 13.5px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  svg { font-size: 16px; }
`;
const ReviewEmailsButton = styled(ButtonFilled)`
  border-radius: 999px;
  padding: 11px 24px;
  font-weight: 600;
  font-size: 13.5px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 6px 16px rgba(43, 82, 231, 0.2);

  svg { font-size: 17px; }

  &:hover, &:focus { box-shadow: 0 8px 20px rgba(43, 82, 231, 0.26); }
`;
const ReviewEmailsRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;
const EmailDraftError = styled.div`
  margin-bottom: 14px;
  font-size: 12.5px;
  color: ${COLORS.ERROR};
`;
const ActiveFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;
const ActiveFilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 11.5px;
  font-weight: 500;
  background: ${(props) => (props.$muted ? "#F5F6FA" : "#E9EDFC")};
  color: ${(props) => (props.$muted ? COLORS.TEXT.PRIMARY : COLORS.PRIMARY)};
`;
const FilterNavButton = styled.button`
  border: 1px solid #ECEFF7;
  background: #fff;
  color: ${COLORS.PRIMARY};
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 11.5px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;

  &:disabled {
    color: ${COLORS.TEXT.PRIMARY};
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
// Scoped to .ai-ranking-modal only (via the Modal's wrapClassName) so this
// never touches any other Modal in the app — always full-viewport, no
// expand toggle needed.
const AiRankingModalStyle = createGlobalStyle`
  .ai-ranking-modal .ant-modal {
    top: 6px;
    padding-bottom: 0;
  }
  .ai-ranking-modal .ant-modal * {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
  .ai-ranking-modal .ant-tabs-nav {
    margin-bottom: 8px;
    flex-shrink: 0;
  }
  /* Let the tab body inherit the modal's full height so the ranking columns
     can stretch instead of stopping at their content height. */
  .ai-ranking-modal .ant-tabs {
    height: 100%;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .ai-ranking-modal .ant-tabs-content-holder,
  .ai-ranking-modal .ant-tabs-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
  .ai-ranking-modal .ant-tabs-tabpane {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
  /* Thin, subtle scrollbars for modern browsers */
  .ai-ranking-modal ::-webkit-scrollbar {
    width: 5px;
  }
  .ai-ranking-modal ::-webkit-scrollbar-track {
    background: transparent;
  }
  .ai-ranking-modal ::-webkit-scrollbar-thumb {
    background: #C5CAD6;
    border-radius: 3px;
  }
  .ai-ranking-modal ::-webkit-scrollbar-thumb:hover {
    background: #A8AEB9;
  }
`;
// Nested inside the ranking modal but rendered as its own top-level antd
// Modal, so it needs the same font override applied separately.
const EmailReviewModalStyle = createGlobalStyle`
  .email-review-modal .ant-modal * {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
`;
// Same story as EmailReviewModalStyle — the Filter & Sort modal also portals
// out to document.body, outside .ai-ranking-modal's scope.
const ListFilterModalStyle = createGlobalStyle`
  .list-filter-modal .ant-modal * {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
`;
const InsightsSection = styled.div`
  margin-bottom: 20px;
  &:last-child { margin-bottom: 0; }
`;
const InsightsTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;
const InsightsSectionTitle = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: ${COLORS.BLACK};
  margin-bottom: 12px;
`;
const InsightsHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
`;
const InsightsIconChip = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$bg || "#E9EDFC"};
  color: ${(props) => props.$color || COLORS.PRIMARY};
  svg { font-size: 16px; }
`;
const InsightsHeaderTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.BLACK};
`;
const InsightsHeaderSubtitle = styled.div`
  font-size: 11px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-top: 2px;
`;
const InsightsIntroTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.BLACK};
`;
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
`;
const KpiTile = styled.div`
  background: #FAFBFE;
  border: 1px solid #ECEFF7;
  border-radius: 8px;
  padding: 12px;
  strong { display: block; font-size: 20px; font-weight: 600; color: ${COLORS.BLACK}; }
  span { font-size: 11px; letter-spacing: 0.2px; color: ${COLORS.TEXT.SECONDARY || COLORS.TEXT.PRIMARY}; }
`;
const TopCandidateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #EEF1F8;
  font-size: 12.5px;
  &:last-child { border-bottom: none; }
`;
const TopCandidateRank = styled.div`
  width: 18px;
  color: ${COLORS.TEXT.PRIMARY};
  flex-shrink: 0;
`;
const TopCandidateName = styled.div`
  flex: 1;
  color: ${COLORS.TEXT.PRIMARY};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const TopCandidateScore = styled.div`
  font-weight: 600;
  color: ${(props) => props.$color};
  flex-shrink: 0;
`;
const EmptyRankState = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 20px;
  min-height: 300px;
`;
const EmptyRankTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.BLACK};
  margin-bottom: 6px;
`;
const EmptyRankText = styled.div`
  font-size: 12.5px;
  color: ${COLORS.TEXT.PRIMARY};
  max-width: 280px;
`;
const RankingSplit = styled.div`
  display: flex;
  align-items: stretch;
  gap: 20px;
  flex: 1;
  min-height: 0;
  overflow-x: auto;
`;
const CandidateListCol = styled.div`
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid #EEF1F8;
  padding-right: 16px;
`;
const CandidateListScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 12px;
`;
const CandidateListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.BLACK};
`;
const CandidateListCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  margin-left: 6px;
  border-radius: 999px;
  background: #E9EDFC;
  color: ${COLORS.PRIMARY};
  font-size: 11px;
  font-weight: 600;
`;
const CandidateListRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  border-left: 3px solid ${(props) => (props.$active ? COLORS.PRIMARY : "transparent")};
  background: ${(props) => (props.$active ? "#F0F3FE" : "transparent")};

  &:hover { background: ${(props) => (props.$active ? "#F0F3FE" : "#FAFBFE")}; }
`;
const CandidateAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #E9EDFC;
  color: ${COLORS.PRIMARY};
  font-size: 11.5px;
  font-weight: 600;
`;
const CandidateListName = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: ${COLORS.BLACK};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const CandidateListMeta = styled.div`
  font-size: 11px;
  color: ${COLORS.TEXT.PRIMARY};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const CandidateListScore = styled.div`
  flex-shrink: 0;
  text-align: right;
  color: ${(props) => props.$color};
  line-height: 1.15;
  strong { display: block; font-size: 14px; font-weight: 700; }
  span { font-size: 9.5px; opacity: .85; }
`;
const ListToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;
const ListSearchBox = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid #E5E9F7;
  border-radius: 8px;
  background: #fff;

  svg { font-size: 16px; color: ${COLORS.TEXT.PRIMARY}; flex-shrink: 0; }
`;
const ListSearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-size: 12px;
  color: ${COLORS.BLACK};
  background: transparent;

  &::placeholder { color: ${COLORS.TEXT.PRIMARY}; }
`;
const ListFilterToggle = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(props) => (props.$active ? COLORS.PRIMARY : "#E5E9F7")};
  border-radius: 8px;
  background: ${(props) => (props.$active ? "#E9EDFC" : "#fff")};
  color: ${(props) => (props.$active ? COLORS.PRIMARY : COLORS.TEXT.PRIMARY)};
  cursor: pointer;

  &:hover { border-color: ${COLORS.PRIMARY}; color: ${COLORS.PRIMARY}; }
  svg { font-size: 17px; }
`;
const ListFilterPanel = styled.div`
  margin-bottom: 12px;
  padding: 14px;
  border: 1px solid #E5E9F7;
  border-radius: 10px;
  background: #FAFBFE;
`;
const ListFilterRow = styled.div`
  margin-bottom: 12px;
  &:last-of-type { margin-bottom: 8px; }
`;
const ListFilterLabel = styled.div`
  font-size: 11.5px;
  font-weight: 600;
  color: ${COLORS.TEXT.PRIMARY};
  margin-bottom: 6px;
`;
const ScoreRangeInputs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  input {
    width: 100%;
    padding: 5px 8px;
    border: 1px solid #E5E9F7;
    border-radius: 6px;
    font-size: 12px;
    outline: none;
    &:focus { border-color: ${COLORS.PRIMARY}; }
  }
  span { color: ${COLORS.TEXT.PRIMARY}; font-size: 12px; }
`;
const ListFilterSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    border-radius: 6px !important;
    font-size: 12px !important;
  }
`;
const ClearFiltersLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: none;
  padding: 0;
  color: ${COLORS.PRIMARY};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  svg { font-size: 14px; }
  &:hover { text-decoration: underline; }
`;
const ListPaginationBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #EEF1F8;
`;
const ListPaginationInfo = styled.div`
  font-size: 11.5px;
  color: ${COLORS.TEXT.PRIMARY};
`;
const ListPaginationButtons = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;
const ListPageEllipsis = styled.span`
  padding: 0 2px;
  font-size: 11.5px;
  color: ${COLORS.TEXT.PRIMARY};
`;
const ListPageArrow = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #E5E9F7;
  border-radius: 6px;
  background: #fff;
  color: ${COLORS.TEXT.PRIMARY};
  cursor: pointer;

  &:disabled { opacity: .4; cursor: default; }
  &:hover:not(:disabled) { border-color: ${COLORS.PRIMARY}; color: ${COLORS.PRIMARY}; }
  svg { font-size: 15px; }
`;
const ListPageNumber = styled.button`
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(props) => (props.$active ? COLORS.PRIMARY : "#E5E9F7")};
  border-radius: 6px;
  background: ${(props) => (props.$active ? COLORS.PRIMARY : "#fff")};
  color: ${(props) => (props.$active ? "#fff" : COLORS.TEXT.PRIMARY)};
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { border-color: ${COLORS.PRIMARY}; }
`;
const DetailCol = styled.div`
  flex: 1;
  min-width: 260px;
  min-height: 0;
  overflow-y: auto;
  padding-right: 12px;
`;
const DetailHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 4px;
`;
const RankPill = styled.div`
  margin-left: auto;
  background: #E9EDFC;
  color: ${COLORS.PRIMARY};
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  padding: 4px 10px;
  flex-shrink: 0;
`;
const DetailMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 12px;
  color: ${COLORS.TEXT.PRIMARY};
  margin: 4px 0 10px;
  svg { font-size: 14px; vertical-align: -2px; margin-right: 3px; }
`;
const MatchBarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
`;
const MatchBarTile = styled.div`
  background: #FAFBFE;
  border: 1px solid #ECEFF7;
  border-radius: 8px;
  padding: 10px 12px;
`;
const MatchBarLabel = styled.div`
  font-size: 11px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-bottom: 4px;
`;
const MatchBarValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.BLACK};
  margin-bottom: 4px;
`;
const MatchBarTrack = styled.div`
  height: 5px;
  border-radius: 3px;
  background: #ECEFF7;
  overflow: hidden;
`;
const MatchBarFill = styled.div`
  height: 100%;
  border-radius: 3px;
  width: ${(props) => Math.max(0, Math.min(100, props.$pct || 0))}%;
  background: ${(props) => props.$color};
`;
const SkillTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const SkillTag = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 11.5px;
  font-weight: 500;
  background: ${(props) => (props.$tone === "warning" ? "#FFF3DC" : "#EEF4FF")};
  color: ${(props) => (props.$tone === "warning" ? "#8A5A00" : COLORS.PRIMARY)};
`;
const SkillBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  &:last-child { margin-bottom: 0; }
`;
const SkillBarLabel = styled.div`
  width: 74px;
  flex-shrink: 0;
  font-size: 12px;
  color: ${COLORS.TEXT.PRIMARY};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const SkillBarTrack = styled.div`
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: #ECEFF7;
  overflow: hidden;
`;
const SkillBarFill = styled.div`
  height: 100%;
  border-radius: 4px;
  width: ${(props) => Math.max(0, Math.min(100, props.$pct || 0))}%;
  background: ${(props) => props.$color || COLORS.PRIMARY};
`;
const SkillBarPct = styled.div`
  width: 34px;
  flex-shrink: 0;
  text-align: right;
  font-size: 11.5px;
  font-weight: 600;
  color: ${COLORS.BLACK};
`;
const InsightNoteCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #EEF4FF;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 12px;
  line-height: 1.5;
  color: #1F3FB0;
`;
const KpiIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  margin-bottom: 6px;
  background: ${(props) => props.$bg || "#E9EDFC"};
  color: ${(props) => props.$color || COLORS.PRIMARY};
  svg { font-size: 13px; }
`;
const JobSwitcher = styled(Select)`
  min-width: 160px;
  max-width: 220px;
  margin-left: 4px;

  .ant-select-selector {
    border-radius: 999px !important;
    font-size: 12px !important;
  }
`;
const ExpandToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${COLORS.PRIMARY};
  font-size: 11px;
  svg {
    font-size: 16px;
    transition: transform 0.15s ease;
    transform: rotate(${(props) => (props.$expanded ? "180deg" : "0deg")});
  }
`;
const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;
const DetailBlock = styled.div`
  border-left: 3px solid ${(props) => (props.$tone === "warning" ? COLORS.WARNING : COLORS.SUCCESS)};
  background: ${(props) => (props.$tone === "warning" ? "#FFF7E6" : "#E8F8EF")};
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0; }
  ul { margin: 4px 0 0; padding-left: 16px; }
  li { margin-bottom: 3px; }
`;
const DetailBlockTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
  color: ${(props) => (props.$tone === "warning" ? "#8A5A00" : "#0B6B3A")};
  svg { font-size: 14px; }
`;
const RankScore = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid ${(props) => props.$color};
  color: ${(props) => props.$color};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 1;
  strong { font-size: 14px; font-weight: 700; }
  span { font-size: 7px; opacity: .8; }
`;

const StyledViewMenu = styled.div`
  width: 150px;
  cursor: pointer;
`;
const Styledtotal = styled.div`
  @media (max-width: 320px) {
    .search-container {
      display: block !important;
    }
  }
`;
const StyledDropdownMenu = styled(Dropdown)`
  width: 150px;
  cursor: pointer;
`;

function JobsList({
  data,
  loading,
  candidateList,
  setShowComponent,
  getData,
  setSelectedItem,
  MenuList,
  searchTerm,
  setSearchTerm,
  onRankingContextChange,
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [isLoading, setIsloading] = useState(false);
  const [rankLoadingJobId, setRankLoadingJobId] = useState(null);
  const [rankResult, setRankResult] = useState(null);
  const [rankProgress, setRankProgress] = useState(null);
  const [expandedCandidateId, setExpandedCandidateId] = useState(null);
  const [rankJobId, setRankJobId] = useState(null);
  const [rankModalOpen, setRankModalOpen] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  // Mirrors RecruitmentChatPanel's single in-flight AI action (analysis,
  // ranking, email, status update, ...) — rendered here in Section 1
  // (Playground) instead of inside the chat thread.
  // Mirror of every action card the chat panel holds, plus which one
  // Section 1 is currently pointed at.
  const [progressCardsById, setProgressCardsById] = useState({});
  const [shownProgressId, setShownProgressId] = useState(null);
  const progressCardsRef = useRef({});
  const liveProgressIdRef = useRef(null);
  // Report navigations await a (possibly slow) re-rank before switching the
  // view. Without this token an earlier click's tail can land after a later
  // one and drag Section 1 back to the report the recruiter already left.
  const sectionNavTokenRef = useRef(0);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [rankModalTab, setRankModalTab] = useState("list");
  const [rankShowInsights, setRankShowInsights] = useState(true);
  // Workflow Agents is a standalone view of Section 1, not a peer of the
  // Ranking/Insights tabs — it has nothing to do with a specific job's
  // ranking results, so it fully replaces that content instead of joining it.
  const [showWorkflowAgents, setShowWorkflowAgents] = useState(false);
  const [agentSettingsOpen, setAgentSettingsOpen] = useState(false);
  // When an Email Report is opened (generated or reopened from a chat
  // card), Section 1 shows ONLY the Email tab — Ranking/Insights headers
  // hide entirely rather than just losing focus. A fresh rank/insights
  // action from chat (or the job switcher) restores the full tab set.
  const [emailOnlyView, setEmailOnlyView] = useState(false);
  // Generated/reopened email reports show in a dedicated review Modal
  // instead of inline in Section 1 — inline space stays reserved for the
  // progress card (and a small "Review Emails" prompt once it's done),
  // so the drafts list/errors never end up stacked messily underneath it.
  const [emailReviewModalOpen, setEmailReviewModalOpen] = useState(false);
  // Browser-history-style stack: filterHistory[i] is the candidate list
  // after applying that step's filter on top of the previous step (or on
  // top of the original ranking, for step 0). filterIndex === -1 means
  // "showing the original, unfiltered ranking."
  const [filterHistory, setFilterHistory] = useState([]);
  const [filterIndex, setFilterIndex] = useState(-1);
  const [emailCandidate, setEmailCandidate] = useState(null);
  const [emailPurpose, setEmailPurpose] = useState("interview_invitation");
  const [emailTone, setEmailTone] = useState("professional");
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailGenerated, setEmailGenerated] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(null);
  // Email Report — a chat-triggered bulk-draft result. Keyed by a unique
  // reportId (NOT jobId) because a recruiter can generate several email
  // reports for the same job with different conditions ("shortlisted with
  // score above 8", then later "all candidates above 5") — keying by jobId
  // alone meant every new report for that job silently overwrote the last
  // one, so an older chat card's "View report" would reopen whatever the
  // LATEST report happened to be instead of the snapshot it actually
  // represents. activeEmailReportId is whichever one Section 1 is showing.
  const [emailReportsById, setEmailReportsById] = useState({});
  const [activeEmailReportId, setActiveEmailReportId] = useState(null);
  const [bulkReviewIndex, setBulkReviewIndex] = useState(0);
  // sendAllEmails (called from the chat panel, often seconds after the
  // report finished generating) must never read a stale closure over
  // emailReportsById — mirror it into a ref so it's always current.
  const emailReportsByIdRef = useRef(emailReportsById);
  useEffect(() => {
    emailReportsByIdRef.current = emailReportsById;
  }, [emailReportsById]);
  const { t } = useTranslation();

  const resetFilters = () => {
    setFilterHistory([]);
    setFilterIndex(-1);
  };

  const closeRankModal = () => {
    setRankModalOpen(false);
    setRankResult(null);
    setExpandedCandidateId(null);
    setRankJobId(null);
    resetFilters();
    closeEmailComposer();
  };

  const displayedCandidates =
    filterIndex === -1 ? (rankResult?.ranked || []) : filterHistory[filterIndex]?.candidates || [];

  // Summary of whichever email report is currently open for this job — folded
  // into the chat's context so "these candidates"/"send it"/"how many did we
  // email" resolve without the recruiter re-stating what's already on screen.
  const activeEmailReportForChat = (() => {
    const report = activeEmailReportId ? emailReportsById[activeEmailReportId] : null;
    if (!report || report.jobId !== rankJobId) return null;
    return {
      jobTitle: report.jobTitle,
      description: report.description || null,
      totalCount: report.drafts.length,
      sentCount: report.drafts.filter((d) => d.sent).length,
      candidateNames: report.drafts.map((d) => d.candidateName),
    };
  })();

  // The bulk resume-analysis ProgressCard shown in Section 1 while a full
  // job ranking is running — computed once here so the exact same card
  // object can also be mirrored (as a MiniProgressCard) into the chat
  // panel, instead of the two ever drifting out of sync with each other.
  const bulkAnalysisCard = (() => {
    if (!(rankLoadingJobId && rankLoadingJobId === rankJobId) || rankResult) return null;
    const total = rankProgress?.total || 0;
    const completed = rankProgress?.completed || 0;
    const remaining = Math.max(total - completed, 0);
    const percent = total ? Math.round((completed / total) * 100) : 0;
    const jobTitle = (data || []).find((j) => j.jobId === rankJobId)?.title;
    const STEPS = [
      t("Reading resume"),
      t("Extracting details"),
      t("Matching skills"),
      t("Evaluating experience"),
      t("Generating insights"),
    ];
    const stepIndex = Math.min(STEPS.length - 1, Math.floor((percent / 100) * STEPS.length));
    return {
      icon: AutoAwesomeIcon,
      title: t("Analyzing Resumes"),
      description: t("We're reviewing each candidate's resume and extracting key details to find the best matches."),
      metaLabel: t("Overall analysis progress"),
      eta: remaining ? t("{{remaining}} remaining · ~{{sec}} sec", { remaining, sec: Math.max(5, remaining * 10) }) : null,
      steps: STEPS,
      activeIndex: stepIndex,
      completed: false,
      awaitingApproval: false,
      bulk: total ? { current: completed, total, unit: completed === 1 ? t("resume") : t("resumes"), verb: t("analyzed") } : null,
      currentItem: rankProgress?.candidateName
        ? {
            title: rankProgress.candidateName,
            subtitle: jobTitle,
            meta: t("Resume {{n}} of {{total}}", { n: Math.min(completed + 1, total), total }),
          }
        : null,
      note: remaining
        ? t("Analyzing {{completed}} of {{total}} resumes. {{remaining}} remaining. This may take a few seconds.", { completed, total, remaining })
        : null,
    };
  })();

  // Keep the master-detail selection valid as the ranked/filtered list
  // changes — default to the top candidate, same as the screenshot design.
  useEffect(() => {
    if (!displayedCandidates.length) {
      if (selectedCandidateId !== null) setSelectedCandidateId(null);
      return;
    }
    if (!displayedCandidates.some((c) => c.candidateId === selectedCandidateId)) {
      setSelectedCandidateId(displayedCandidates[0].candidateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedCandidates]);

  // Search/sort/filter/pagination for the ranked-candidates list — layered
  // on top of displayedCandidates (which is already whatever the AI chat's
  // ranking/filter conversation produced). Selection/detail view below still
  // reads from the full displayedCandidates, only the left-column rows and
  // pagination are affected by this.
  const EXPERIENCE_BUCKETS = [
    { value: "all", label: t("All") },
    { value: "0-2", label: t("0 - 2 yrs") },
    { value: "3-5", label: t("3 - 5 yrs") },
    { value: "6-8", label: t("6 - 8 yrs") },
    { value: "9+", label: t("9+ yrs") },
  ];
  const LIST_PAGE_SIZE = 8;

  const [listSearchTerm, setListSearchTerm] = useState("");
  const [listSortBy, setListSortBy] = useState("rank");
  const [listFilterPanelOpen, setListFilterPanelOpen] = useState(false);
  const [listScoreRange, setListScoreRange] = useState([0, 100]);
  const [listExperienceFilter, setListExperienceFilter] = useState("all");
  const [listLocationFilter, setListLocationFilter] = useState("all");
  const [listSkillFilter, setListSkillFilter] = useState("all");
  const [listStatusFilter, setListStatusFilter] = useState("all");

  // Pending filter state (staged changes while modal is open)
  const [pendingListSortBy, setPendingListSortBy] = useState("rank");
  const [pendingListScoreRange, setPendingListScoreRange] = useState([0, 100]);
  const [pendingListExperienceFilter, setPendingListExperienceFilter] = useState("all");
  const [pendingListLocationFilter, setPendingListLocationFilter] = useState("all");
  const [pendingListSkillFilter, setPendingListSkillFilter] = useState("all");
  const [pendingListStatusFilter, setPendingListStatusFilter] = useState("all");
  const [listPage, setListPage] = useState(1);

  const matchesExperienceBucket = (years, bucket) => {
    if (bucket === "all") return true;
    if (typeof years !== "number") return false;
    if (bucket === "0-2") return years <= 2;
    if (bucket === "3-5") return years >= 3 && years <= 5;
    if (bucket === "6-8") return years >= 6 && years <= 8;
    if (bucket === "9+") return years >= 9;
    return true;
  };

  const candidateStatusBucket = (candidateId) => {
    const full = filter(candidateList, (c) => c.candidateId === candidateId)[0];
    const status = full?.currentStatus;
    return isUndefined(status) || isEmpty(status) || status === "0" ? "new" : "reviewed";
  };

  const listLocationOptions = [
    { value: "all", label: t("All") },
    ...uniq(
      displayedCandidates.map((c) => candidateLocationLabel(candidateList, c.candidateId)).filter(Boolean)
    ).map((loc) => ({ value: loc, label: loc })),
  ];
  const listSkillOptions = [
    { value: "all", label: t("All") },
    ...uniq(displayedCandidates.flatMap((c) => c.skills || [])).map((skill) => ({ value: skill, label: skill })),
  ];
  const listStatusOptions = [
    { value: "all", label: t("All") },
    { value: "new", label: t("New") },
    { value: "reviewed", label: t("Reviewed") },
  ];

  const listFiltersActive =
    listScoreRange[0] !== 0 ||
    listScoreRange[1] !== 100 ||
    listExperienceFilter !== "all" ||
    listLocationFilter !== "all" ||
    listSkillFilter !== "all" ||
    listStatusFilter !== "all";

  // Initialize pending state when filter modal opens
  useEffect(() => {
    if (listFilterPanelOpen) {
      setPendingListSortBy(listSortBy);
      setPendingListScoreRange([...listScoreRange]);
      setPendingListExperienceFilter(listExperienceFilter);
      setPendingListLocationFilter(listLocationFilter);
      setPendingListSkillFilter(listSkillFilter);
      setPendingListStatusFilter(listStatusFilter);
    }
  }, [listFilterPanelOpen]);

  const clearListFilters = () => {
    setPendingListScoreRange([0, 100]);
    setPendingListExperienceFilter("all");
    setPendingListLocationFilter("all");
    setPendingListSkillFilter("all");
    setPendingListStatusFilter("all");
  };

  const applyListFilters = () => {
    setListSortBy(pendingListSortBy);
    setListScoreRange([...pendingListScoreRange]);
    setListExperienceFilter(pendingListExperienceFilter);
    setListLocationFilter(pendingListLocationFilter);
    setListSkillFilter(pendingListSkillFilter);
    setListStatusFilter(pendingListStatusFilter);
    setListFilterPanelOpen(false);
  };

  const discardListFilters = () => {
    setListFilterPanelOpen(false);
  };

  const filteredListCandidates = displayedCandidates
    .filter((c) => {
      const term = listSearchTerm.trim().toLowerCase();
      if (!term) return true;
      const nameMatch = c.candidateName?.toLowerCase().includes(term);
      const skillMatch = (c.skills || []).some((s) => s.toLowerCase().includes(term));
      return nameMatch || skillMatch;
    })
    .filter((c) => {
      const score100 = typeof c.score === "number" ? c.score * 10 : null;
      if (score100 === null) return true;
      return score100 >= listScoreRange[0] && score100 <= listScoreRange[1];
    })
    .filter((c) => matchesExperienceBucket(c.yearsExperience, listExperienceFilter))
    .filter(
      (c) => listLocationFilter === "all" || candidateLocationLabel(candidateList, c.candidateId) === listLocationFilter
    )
    .filter((c) => listSkillFilter === "all" || (c.skills || []).includes(listSkillFilter))
    .filter((c) => listStatusFilter === "all" || candidateStatusBucket(c.candidateId) === listStatusFilter);

  const sortedListCandidates = [...filteredListCandidates].sort((a, b) => {
    if (listSortBy === "score") return (b.score ?? 0) - (a.score ?? 0);
    if (listSortBy === "name") return (a.candidateName || "").localeCompare(b.candidateName || "");
    if (listSortBy === "experience") return (b.yearsExperience ?? 0) - (a.yearsExperience ?? 0);
    return 0;
  });

  const listTotalPages = Math.max(1, Math.ceil(sortedListCandidates.length / LIST_PAGE_SIZE));
  const listPageSafe = Math.min(listPage, listTotalPages);
  const pageCandidates = sortedListCandidates.slice(
    (listPageSafe - 1) * LIST_PAGE_SIZE,
    listPageSafe * LIST_PAGE_SIZE
  );

  // Windowed page numbers (current ±1, plus first/last) with "…" gaps —
  // keeps the pager to a couple of rows even when there are 10+ pages,
  // instead of one button per page overflowing the 280px-wide column.
  const listPageWindow = [];
  if (listTotalPages <= 7) {
    for (let p = 1; p <= listTotalPages; p++) listPageWindow.push(p);
  } else {
    const set = new Set([1, 2, listTotalPages - 1, listTotalPages, listPageSafe - 1, listPageSafe, listPageSafe + 1]);
    const sorted = [...set].filter((p) => p >= 1 && p <= listTotalPages).sort((a, b) => a - b);
    sorted.forEach((p, i) => {
      if (i > 0 && p - sorted[i - 1] > 1) listPageWindow.push("…");
      listPageWindow.push(p);
    });
  }

  useEffect(() => {
    setListPage(1);
  }, [
    listSearchTerm,
    listSortBy,
    listScoreRange,
    listExperienceFilter,
    listLocationFilter,
    listSkillFilter,
    listStatusFilter,
    rankJobId,
    filterIndex,
  ]);

  // Details (match bars/strengths/gaps) default to open for whichever
  // candidate is selected — matches the reference design. Manually hiding
  // them (via "Hide details") stays collapsed until a different candidate
  // is picked.
  useEffect(() => {
    setExpandedCandidateId(selectedCandidateId);
  }, [selectedCandidateId]);

  const handleFilterBack = () => {
    setFilterIndex((prev) => Math.max(-1, prev - 1));
  };

  const openEmailComposer = (candidate) => {
    const fullCandidate = filter(
      candidateList,
      (c) => c.candidateId === candidate.candidateId
    )[0];
    setEmailCandidate(candidate);
    setEmailPurpose("interview_invitation");
    setEmailTone("professional");
    setEmailRecipient(fullCandidate?.emailId || "");
    setEmailSubject("");
    setEmailBody("");
    setEmailGenerated(false);
    setEmailError(null);
  };

  const closeEmailComposer = () => {
    setEmailCandidate(null);
    setEmailLoading(false);
    setEmailError(null);
  };

  const handleGenerateEmail = async () => {
    if (!emailCandidate || emailLoading) return;
    setEmailLoading(true);
    setEmailError(null);
    try {
      const result = await draftEmailForCandidate(emailCandidate, emailPurpose, emailTone);
      if (result.error || !result.subject) {
        setEmailError(result.error || t("Could not draft an email. Please try again."));
        return;
      }
      setEmailSubject(result.subject);
      setEmailBody(result.body);
      setEmailGenerated(true);
    } catch (error) {
      console.error("Draft candidate email error:", error);
      setEmailError(t("Sorry, couldn't draft that email. Please try again."));
    } finally {
      setEmailLoading(false);
    }
  };

  const draftEmailForCandidate = (candidate, purpose, tone, customInstructions, jobTitleOverride) =>
    callRecruitmentAIAction("draft_candidate_email", {
      candidateName: candidate.candidateName,
      jobTitle: jobTitleOverride || rankResult?.jobTitle,
      purpose,
      tone,
      customInstructions,
      score: candidate.score,
      summary: candidate.summary,
      strengths: candidate.strengths,
      gaps: candidate.gaps,
      skills: candidate.skills,
      yearsExperience: candidate.yearsExperience,
    });

  const buildDraftsSeed = (candidates) =>
    candidates.map((c) => {
      const full = filter(candidateList, (cd) => cd.candidateId === c.candidateId)[0];
      return {
        candidateId: c.candidateId,
        candidateName: c.candidateName,
        candidate: c,
        recipient: full?.emailId || "",
        subject: "",
        body: "",
        generated: false,
        loading: false,
        error: null,
        sending: false,
        sent: false,
      };
    });

  const updateEmailDraft = (reportId, candidateId, patch) => {
    setEmailReportsById((prev) => {
      const report = prev[reportId];
      if (!report) return prev;
      return {
        ...prev,
        [reportId]: {
          ...report,
          drafts: report.drafts.map((d) => (d.candidateId === candidateId ? { ...d, ...patch } : d)),
        },
      };
    });
  };

  // Simulated send — no email-sending backend exists yet for candidates
  // (same "preview only" reality the old composers already stated). Just
  // flips local UI state so the recruiter can review then mark each one
  // sent as they step through the report.
  const handleSendEmail = async (reportId, candidateId) => {
    updateEmailDraft(reportId, candidateId, { sending: true });
    await new Promise((resolve) => setTimeout(resolve, 500));
    updateEmailDraft(reportId, candidateId, { sending: false, sent: true });
  };

  // Auto Mode / the "Approve & Send" bulk-approval card both send every
  // still-unsent draft in one go — Section 1 shows each one flip to Sent as
  // handleSendEmail resolves, since it's reading the same shared state.
  const sendAllEmails = async (reportId) => {
    const report = emailReportsByIdRef.current[reportId];
    if (!report) return;
    const pending = report.drafts.filter((d) => d.generated && !d.sent && !d.sending);
    await Promise.all(pending.map((d) => handleSendEmail(reportId, d.candidateId)));
  };

  // Reopens a specific past email report (from its chat card) without
  // regenerating it — switches Section 1's job context to match (cheap,
  // reads the ranking cache) then shows that exact report snapshot.
  const openEmailReport = async (reportId) => {
    const report = emailReportsByIdRef.current[reportId];
    if (!report) return;
    const record = (data || []).find((j) => j.jobId === report.jobId);
    if (!record) return;
    const token = ++sectionNavTokenRef.current;
    setActiveEmailReportId(reportId);
    setBulkReviewIndex(0);
    await handleRankCandidates(record);
    if (sectionNavTokenRef.current !== token) return;
    setEmailOnlyView(true);
    setRankModalTab("email");
    setEmailReviewModalOpen(true);
  };

  // Called by the docked chat panel when it detects an email-drafting
  // request (parse_email_request). Filters the currently-ranked candidates,
  // drafts an email per match, and stores the result under a fresh reportId
  // — a recruiter can run several differently-worded email requests against
  // the same job, and each keeps its own snapshot so its chat card reopens
  // exactly what it generated instead of whatever the latest one is.
  const requestBulkEmailFromChat = async (emailIntent) => {
    const jobId = rankJobId;
    const jobTitle = rankResult?.jobTitle;
    let matches = applyFilterSpec(displayedCandidates, emailIntent.filter, candidateList);
    if (emailIntent.topN) {
      matches = [...matches]
        .filter((c) => typeof c.score === "number")
        .sort((a, b) => b.score - a.score)
        .slice(0, emailIntent.topN);
    }
    if (!matches.length) {
      return { ok: false, message: t("No candidates matched that request.") };
    }

    const reportId = `email-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const draftsSeed = buildDraftsSeed(matches);
    setBulkReviewIndex(0);
    setEmailReportsById((prev) => ({
      ...prev,
      [reportId]: { reportId, jobId, jobTitle, description: emailIntent.description, drafts: draftsSeed },
    }));
    setActiveEmailReportId(reportId);
    setEmailOnlyView(true);
    setRankModalTab("email");

    await Promise.all(
      draftsSeed.map(async (draft) => {
        updateEmailDraft(reportId, draft.candidateId, { loading: true, error: null });
        try {
          const result = await draftEmailForCandidate(
            draft.candidate,
            emailIntent.purpose,
            emailIntent.tone,
            emailIntent.customInstructions || "",
            jobTitle
          );
          if (result.error || !result.subject) {
            updateEmailDraft(reportId, draft.candidateId, {
              loading: false,
              error: result.error || t("Could not draft an email."),
            });
            return;
          }
          updateEmailDraft(reportId, draft.candidateId, {
            loading: false,
            generated: true,
            subject: result.subject,
            body: result.body,
          });
        } catch (error) {
          console.error("Bulk draft email error:", error);
          updateEmailDraft(reportId, draft.candidateId, {
            loading: false,
            error: t("Sorry, couldn't draft this email."),
          });
        }
      })
    );

    return { ok: true, count: draftsSeed.length, jobId, jobTitle, reportId, description: emailIntent.description };
  };

  // Lift the current ranking/filter state up to Recruitment/index.js, which
  // forwards it into the floating chatbot — that's how it grounds answers in
  // the currently ranked/filtered candidates and offers the bulk-email
  // trigger above, without JobsList needing its own separate chat UI.
  useEffect(() => {
    if (!onRankingContextChange) return;
    onRankingContextChange({
      active: Boolean(rankResult),
      jobId: rankJobId,
      jobTitle: rankResult?.jobTitle,
      candidates: displayedCandidates,
      filterDescription: filterIndex > -1 ? filterHistory[filterIndex]?.description : null,
      requestBulkEmail: requestBulkEmailFromChat,
      openModal: () => setRankModalOpen(true),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankResult, rankJobId, filterIndex, filterHistory]);

  const handleRankCandidates = async (record) => {
    const signature = rankSignature(candidateList, record?.jobId);
    const cached = readRankCache(record?.jobId);
    setRankJobId(record?.jobId);
    setRankModalOpen(true);
    setRankModalTab("list");
    resetFilters();
    closeEmailComposer();
    if (cached && cached.signature === signature) {
      setRankResult(cached.result);
      return;
    }

    setRankLoadingJobId(record?.jobId);
    setRankProgress(null);
    try {
      const result = await callRecruitmentAIAction(
        "rank_candidates",
        { jobId: record?.jobId },
        (p) => setRankProgress(p)
      );
      if (result.error) {
        throw new Error(result.error);
      }
      setRankResult(result);
      writeRankCache(record?.jobId, signature, result);
    } catch (error) {
      console.error("Rank candidates error:", error);
      message.error(error?.message || t("Could not rank candidates. Please try again."));
      throw error;
    } finally {
      setRankLoadingJobId(null);
      setRankProgress(null);
    }
  };

  // Returns the handleRankCandidates promise so the docked chat panel can
  // await completion (to show the "report generated" card only on success)
  // — used both for the initial rank/insights action and to reopen an
  // already-cached report when its chat card is clicked again later.
  // handleRankCandidates always resets the tab to "list" itself (it's also
  // the entry point for a plain job switch) — so the insights/email tab
  // switch has to happen AFTER it resolves, not before, or it gets clobbered.
  // Once its own destination has been viewed, a completed action's card no
  // longer needs to float above whatever the recruiter is looking at now
  // (it stays in the chat as history). The chat panel only re-mirrors on its
  // own state changes, so clearing the local copy sticks until the next
  // action. Uses the updater form — these callbacks are captured inside
  // long-lived progress-card closures, where a read of `actionProgress`
  // would be whatever it was when the card was created.
  const dismissCompletedProgress = () => {
    setShownProgressId((prev) => {
      const card = prev ? progressCardsRef.current[prev] : null;
      return card?.completed ? null : prev;
    });
  };

  // Section 1 shows exactly one thing: an action's progress card, or the
  // report tabs — never both stacked on top of each other. It tracks the
  // card by id and reads it back out of the live registry, so a card that
  // is still running (or gets approved later) keeps updating here.
  progressCardsRef.current = progressCardsById;
  const visibleProgressCard = shownProgressId ? progressCardsById[shownProgressId] || null : null;

  // "View progress" on any chat card (and opening an Email Report) points
  // Section 1 at that action.
  const handleShowProgress = (card) => {
    if (card?.id) setShownProgressId(card.id);
  };

  // A newly started action takes over Section 1; patches to the one already
  // showing must not yank focus away from a card the recruiter opened.
  const handleActionProgressChange = (card) => {
    if (!card) {
      liveProgressIdRef.current = null;
      setShownProgressId(null);
      return;
    }
    if (liveProgressIdRef.current !== card.id) {
      liveProgressIdRef.current = card.id;
      setShownProgressId(card.id);
    }
  };

  const handleChatSelectJob = async (jobId, mode) => {
    const record = (data || []).find((j) => j.jobId === jobId);
    if (!record) return;
    const token = ++sectionNavTokenRef.current;
    dismissCompletedProgress();
    setRankShowInsights(mode !== "rank");
    setEmailOnlyView(false);
    setEmailReviewModalOpen(false);
    await handleRankCandidates(record);
    if (sectionNavTokenRef.current !== token) return;
    setRankModalTab(mode === "insights" ? "insights" : "list");
  };

  // Lets a chat action (e.g. "Move X to Interview") jump back to that
  // candidate in the Ranking tab instead of leaving its progress card as
  // a dead end with nowhere to go.
  const handleViewCandidate = (candidateId) => {
    if (!candidateId) return;
    dismissCompletedProgress();
    setEmailOnlyView(false);
    setEmailReviewModalOpen(false);
    setRankModalTab("list");
    setSelectedCandidateId(candidateId);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e?.target?.value);
  };

  const filteredDataSource = data?.filter((item) =>
    item?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const deleteData = async () => {
    setIsloading(true);
    await CALL_API(`recruitment/jobs/${deleteId}`, "patch", {
      deleteStatus: true,
    });
    getData();
    setIsloading(false);
    setDeleteOpen(false);
  };

  const columns = [
    {
      title: t("Job Title"),
      dataIndex: "title",
      key: "title",
    },
    {
      title: t("New Candidate"),
      dataIndex: "newCandidate",
      key: "newCandidate",
      render: (_, record) => {
        const newUser = filter(candidateList, (candidate) => {
          const status = candidate.currentStatus;
          return (
            (isUndefined(status) || isEmpty(status) || status === "0") &&
            record?.jobId === candidate?.jobId
          );
        }).length;
        return newUser;
      },
    },
    {
      title: t("Total Candidate"),
      dataIndex: "totalCandidate",
      key: "totalCandidate",
      render: (_, record) => {
        const totalUser = filter(candidateList, (candidate) => {
          return candidate?.jobId.toString() === record?.jobId.toString();
        }).length;
        return totalUser;
      },
    },
    {
      title: t("Created On"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (_, record) => {
        const date = moment(record?.createdAt).format("DD MMM YYYY");
        return date;
      },
    },
    {
      title: "",
      dataIndex: "createdOn",
      key: "createdOn",
      render: (_, record) => (
        <Icon
          name="linkicon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          
            const url = `https://hr.dev-wailasoft.foresyts.ai/form/${record?.clientId}/${record?.jobId}`;
            navigator.clipboard.writeText(url);
            message.success(t("Link Copied"));
          }}
        />
      ),
    },
    {
      title: "",
      dataIndex: "createdOn",
      key: "createdOn",
      render: (_, record) => (
        <StyledDropdownMenu
          overlay={
            <StyledViewMenu>
              <Menu>
                {MenuList?.find(
                  (item) =>
                    item?.key === "HRResources" &&
                    item?.subMenu?.find(
                      (subItem) =>
                        subItem?.key === "recruitment" &&
                        subItem?.permission.edit
                    )
                ) && (
                  <Menu.Item
                    key="2"
                    onClick={() => {
                      setSelectedItem(record);
                      setShowComponent("editJob");
                    }}
                  >
                    {t("Edit the Job")}
                  </Menu.Item>
                )}
                {MenuList?.find(
                  (item) =>
                    item?.key === "HRResources" &&
                    item?.subMenu?.find(
                      (subItem) =>
                        subItem?.key === "recruitment" &&
                        subItem?.permission.delete
                    )
                ) && (
                  <Menu.Item
                    key="4"
                    onClick={() => {
                      setDeleteId(record?.jobId);
                      setDeleteOpen(true);
                    }}
                  >
                    {t("Delete the Job")}
                  </Menu.Item>
                )}
              </Menu>
            </StyledViewMenu>
          }
          trigger={["click"]}
          placement="bottomRight"
        >
          <span
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {MenuList?.find(
              (item) =>
                item?.key === "HRResources" &&
                item?.subMenu?.find(
                  (subItem) =>
                    subItem?.key === "recruitment" &&
                    (subItem?.permission.edit || subItem?.permission.delete)
                )
            ) && <MoreHorizIcon />}
          </span>
        </StyledDropdownMenu>
      ),
    },
  ];

  const styledIcon = { color: `${COLORS.TEXT.PRIMARY}`, fontSize: "18px" };
  const styledInput = { color: `${COLORS.TEXT.PRIMARY}`, fontSize: "16px" };
  return (
    <div>
      <Styledtotal>
        <div className="search-container d-flex justify-content-between">
          <div className="mt-2">
            <Input
              style={styledInput}
              prefix={<SearchOutlined style={styledIcon} />}
              placeholder={t("Search")}
              onChange={handleSearchChange}
              value={searchTerm}
            />
          </div>
          <div>
            {MenuList?.find(
              (item) =>
                item?.key === "HRResources" &&
                item?.subMenu?.find(
                  (subItem) =>
                    subItem?.key === "recruitment" && subItem?.permission.create
                )
            ) && (
              <ButtonFilled onClick={() => setShowComponent("addJob")}>
                {t("+ Add New Job")}
              </ButtonFilled>
            )}
          </div>
        </div>
      </Styledtotal>
      <Table
        dataSource={filteredDataSource}
        columns={columns}
        pagination={false}
        onRow={(record, rowIndex) => {
          return {
            onClick: (e) => {
              if (!e.target.closest(".ant-dropdown-menu")) {
                setSelectedItem(record);
                setShowComponent("candidateDetails");
              }
            },
          };
        }}
        loading={loading}
      />
      <AlertModal
        isModalOpen={deleteOpen}
        handleCancel={() => setDeleteOpen(false)}
        handleOk={deleteData}
        name={t("Are you sure you want to delete this Job?")}
        loading={isLoading}
      />
      <Modal
        open={rankModalOpen}
        closable={false}
        onCancel={closeRankModal}
        footer={null}
        width="99vw"
        wrapClassName="ai-ranking-modal"
        bodyStyle={{ padding: 0 }}
      >
        <AiRankingModalStyle />
        <EmailReviewModalStyle />
        <ListFilterModalStyle />
        <div style={{ display: "flex", gap: 12, minHeight: 360, padding: 10 }}>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              height: "94vh",
              display: "flex",
              flexDirection: "column",
              border: "1px solid #ECEFF7",
              borderRadius: 14,
              overflow: "hidden",
            }}
          >
            <SectionOneHeader>
              <AiTitleWrap>
                <AiBadge>✦</AiBadge>
                {t("Recruitment Agent")}
              </AiTitleWrap>
              <SectionOneHeaderRight>
                {showWorkflowAgents ? (
                  <SectionOneMenuButton type="button" title={t("Back to Ranking")} onClick={() => setShowWorkflowAgents(false)}>
                    <ArrowBackIcon style={{ fontSize: 18 }} />
                  </SectionOneMenuButton>
                ) : (
                  <Dropdown
                    trigger={["click"]}
                    overlay={
                      <StyledViewMenu style={{ width: 190 }}>
                        <Menu>
                          <Menu.Item key="workflow-agents" onClick={() => setShowWorkflowAgents(true)}>
                            <MenuItemLabel>
                              <SmartToyOutlinedIcon style={{ fontSize: 16 }} />
                              {t("Workflow Agents")}
                            </MenuItemLabel>
                          </Menu.Item>
                          <Menu.Item key="agent-settings" onClick={() => setAgentSettingsOpen(true)}>
                            <MenuItemLabel>
                              <SettingsOutlinedIcon style={{ fontSize: 16 }} />
                              {t("Settings")}
                            </MenuItemLabel>
                          </Menu.Item>
                        </Menu>
                      </StyledViewMenu>
                    }
                  >
                    <SectionOneMenuButton type="button" title={t("Menu")}>
                      <MoreHorizIcon />
                    </SectionOneMenuButton>
                  </Dropdown>
                )}
                {!showWorkflowAgents && rankJobId && (
                  <JobSwitcher
                    size="small"
                    value={rankJobId}
                    onChange={(jobId) => {
                      const record = (data || []).find((j) => j.jobId === jobId);
                      if (record) {
                        dismissCompletedProgress();
                        setEmailOnlyView(false);
                        handleRankCandidates(record).catch(() => {});
                      }
                    }}
                    options={(data || [])
                      .filter((j) => filter(candidateList, (c) => c?.jobId?.toString() === j?.jobId?.toString()).length > 0)
                      .map((j) => ({ value: j.jobId, label: j.title }))}
                  />
                )}
                <SectionOneCloseButton type="button" title={t("Close")} onClick={closeRankModal}>
                  <CloseOutlinedIcon />
                </SectionOneCloseButton>
              </SectionOneHeaderRight>
            </SectionOneHeader>

            <Modal
              title={t("Recruitment Agent Settings")}
              open={agentSettingsOpen}
              onCancel={() => setAgentSettingsOpen(false)}
              footer={
                <ButtonFilled type="button" onClick={() => setAgentSettingsOpen(false)}>
                  {t("Close")}
                </ButtonFilled>
              }
              centered
              width={420}
            >
              <div style={{ fontSize: 12.5, color: COLORS.TEXT.PRIMARY, lineHeight: 1.6 }}>
                {t("Agent settings — model, notification, and automation preferences — are coming soon.")}
              </div>
            </Modal>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                padding: "6px 20px 12px",
              }}
            >
            {showWorkflowAgents && <WorkflowAgents />}
            {!showWorkflowAgents && visibleProgressCard && (
              <div style={{ marginBottom: 14 }}>
                <ProgressCard card={visibleProgressCard} />
              </div>
            )}
            {!showWorkflowAgents && bulkAnalysisCard && <ProgressCard card={bulkAnalysisCard} />}
            {!showWorkflowAgents && !rankResult && !(rankLoadingJobId && rankLoadingJobId === rankJobId) && !visibleProgressCard && (
              <EmptyRankState>
                <img src={resumeRankerEmptyImg} alt="" style={{ width: 160, height: "auto", marginBottom: 16 }} />
                <EmptyRankTitle>{t("No analysis yet")}</EmptyRankTitle>
                <EmptyRankText>{t("Ask the AI to analyze a candidate's resume to get started.")}</EmptyRankText>
              </EmptyRankState>
            )}
            {!showWorkflowAgents && rankResult && (emailReviewModalOpen || !visibleProgressCard) && (() => {
          const insights = computeInsights(candidateList, rankJobId, { ranked: displayedCandidates });
          const activeEmailReport = activeEmailReportId ? emailReportsById[activeEmailReportId] : null;
          const emailReport = activeEmailReport && activeEmailReport.jobId === rankJobId ? activeEmailReport : null;
          const tabsNode = (
            <Tabs
              activeKey={rankModalTab}
              onChange={(key) => { dismissCompletedProgress(); setRankModalTab(key); }}
              destroyInactiveTabPane
              animated={false}
              items={[
                !emailOnlyView && {
                  key: "list",
                  label: t("Ranking"),
                  children: (
                    <>
                      {filterHistory.length > 0 && (
                        <ActiveFilterRow>
                          {filterIndex > -1 ? (
                            <ActiveFilterChip title={filterHistory[filterIndex]?.description}>
                              {filterHistory[filterIndex]?.description}
                            </ActiveFilterChip>
                          ) : (
                            <ActiveFilterChip $muted>{t("Original ranking")}</ActiveFilterChip>
                          )}
                          <FilterNavButton type="button" disabled={filterIndex === -1} onClick={handleFilterBack}>
                            {t("Back")}
                          </FilterNavButton>
                          <FilterNavButton type="button" disabled={filterIndex === -1} onClick={resetFilters}>
                            {t("Reset filters")}
                          </FilterNavButton>
                        </ActiveFilterRow>
                      )}

                      {rankResult?.ranked?.length === 0 && <div>{t("No candidates for this job yet.")}</div>}
                      {rankResult?.ranked?.length > 0 && displayedCandidates.length === 0 && (
                        <div>{t("No candidates match this filter.")}</div>
                      )}

                      {displayedCandidates.length > 0 && (() => {
                        const selected =
                          displayedCandidates.find((c) => c.candidateId === selectedCandidateId) ||
                          displayedCandidates[0];
                        const selectedRank = displayedCandidates.findIndex((c) => c.candidateId === selected.candidateId) + 1;
                        const isExpanded = expandedCandidateId === selected.candidateId;
                        const location = candidateLocationLabel(candidateList, selected.candidateId);
                        const matchBars = [
                          { label: t("Skill Match"), value: selected.skillMatch },
                          { label: t("Experience Match"), value: selected.experienceMatch },
                          { label: t("Education Match"), value: selected.educationMatch },
                          { label: t("Project / Domain Relevance"), value: selected.projectMatch },
                        ].filter((m) => typeof m.value === "number");

                        return (
                          <RankingSplit>
                            <CandidateListCol>
                              <CandidateListHeader>
                                <span>{t("Ranked Candidates")}<CandidateListCount>{sortedListCandidates.length}</CandidateListCount></span>
                              </CandidateListHeader>

                              <ListToolbar>
                                <ListSearchBox>
                                  <SearchOutlined />
                                  <ListSearchInput
                                    value={listSearchTerm}
                                    onChange={(e) => setListSearchTerm(e.target.value)}
                                    placeholder={t("Search by name, skills, or keywords...")}
                                  />
                                </ListSearchBox>
                                <ListFilterToggle
                                  type="button"
                                  $active={listFilterPanelOpen || listFiltersActive}
                                  title={t("Filter & Sort")}
                                  onClick={() => setListFilterPanelOpen(true)}
                                >
                                  <TuneOutlinedIcon />
                                </ListFilterToggle>
                              </ListToolbar>

                              <Modal
                                title={t("Filter & Sort")}
                                open={listFilterPanelOpen}
                                onCancel={discardListFilters}
                                wrapClassName="list-filter-modal"
                                centered
                                width={420}
                                footer={
                                  <>
                                    <ClearFiltersLink type="button" onClick={clearListFilters}>
                                      <CloseOutlinedIcon /> {t("Clear Filters")}
                                    </ClearFiltersLink>
                                    <ButtonFilled type="button" onClick={applyListFilters}>
                                      {t("Done")}
                                    </ButtonFilled>
                                  </>
                                }
                              >
                                <ListFilterPanel style={{ border: "none", padding: 0, background: "transparent" }}>
                                  <ListFilterRow>
                                    <ListFilterLabel>{t("Sort By")}</ListFilterLabel>
                                    <ListFilterSelect
                                      size="small"
                                      value={pendingListSortBy}
                                      onChange={setPendingListSortBy}
                                      options={[
                                        { value: "rank", label: t("Rank") },
                                        { value: "score", label: t("Score") },
                                        { value: "name", label: t("Name") },
                                        { value: "experience", label: t("Experience") },
                                      ]}
                                    />
                                  </ListFilterRow>

                                  <ListFilterRow>
                                    <ListFilterLabel>{t("Score Range")}</ListFilterLabel>
                                    <ScoreRangeInputs>
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={pendingListScoreRange[0]}
                                        onChange={(e) =>
                                          setPendingListScoreRange([Number(e.target.value) || 0, pendingListScoreRange[1]])
                                        }
                                      />
                                      <span>—</span>
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={pendingListScoreRange[1]}
                                        onChange={(e) =>
                                          setPendingListScoreRange([pendingListScoreRange[0], Number(e.target.value) || 100])
                                        }
                                      />
                                    </ScoreRangeInputs>
                                  </ListFilterRow>

                                  <ListFilterRow>
                                    <ListFilterLabel>{t("Experience")}</ListFilterLabel>
                                    <ListFilterSelect
                                      size="small"
                                      value={pendingListExperienceFilter}
                                      onChange={setPendingListExperienceFilter}
                                      options={EXPERIENCE_BUCKETS}
                                    />
                                  </ListFilterRow>

                                  <ListFilterRow>
                                    <ListFilterLabel>{t("Location")}</ListFilterLabel>
                                    <ListFilterSelect
                                      size="small"
                                      value={pendingListLocationFilter}
                                      onChange={setPendingListLocationFilter}
                                      options={listLocationOptions}
                                    />
                                  </ListFilterRow>

                                  <ListFilterRow>
                                    <ListFilterLabel>{t("Skills")}</ListFilterLabel>
                                    <ListFilterSelect
                                      size="small"
                                      value={pendingListSkillFilter}
                                      onChange={setPendingListSkillFilter}
                                      options={listSkillOptions}
                                    />
                                  </ListFilterRow>

                                  <ListFilterRow>
                                    <ListFilterLabel>{t("Status")}</ListFilterLabel>
                                    <ListFilterSelect
                                      size="small"
                                      value={pendingListStatusFilter}
                                      onChange={setPendingListStatusFilter}
                                      options={listStatusOptions}
                                    />
                                  </ListFilterRow>
                                </ListFilterPanel>
                              </Modal>

                              <CandidateListScroll>
                                {pageCandidates.length === 0 && (
                                  <div style={{ fontSize: 12, color: COLORS.TEXT.PRIMARY, padding: "8px 0" }}>
                                    {t("No candidates match your search/filters.")}
                                  </div>
                                )}
                                {pageCandidates.map((candidate) => (
                                  <CandidateListRow
                                    key={candidate.candidateId}
                                    $active={candidate.candidateId === selected.candidateId}
                                    onClick={() => setSelectedCandidateId(candidate.candidateId)}
                                  >
                                    <CandidateAvatar>
                                      {candidate.candidateName?.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?"}
                                    </CandidateAvatar>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <CandidateListName title={candidate.candidateName}>{candidate.candidateName}</CandidateListName>
                                      <CandidateListMeta>{rankResult.jobTitle}</CandidateListMeta>
                                      <CandidateListMeta>
                                        {candidateLocationLabel(candidateList, candidate.candidateId) && (
                                          <>
                                            <PlaceOutlinedIcon style={{ fontSize: 12, verticalAlign: -2, marginRight: 2 }} />
                                            {candidateLocationLabel(candidateList, candidate.candidateId)}
                                            {typeof candidate.yearsExperience === "number" ? ` · ${candidate.yearsExperience} yrs` : ""}
                                          </>
                                        )}
                                      </CandidateListMeta>
                                    </div>
                                    <CandidateListScore $color={scoreColor(candidate.score)}>
                                      <strong>{formatScore100(candidate.score)}</strong>
                                      <span>/100</span>
                                    </CandidateListScore>
                                  </CandidateListRow>
                                ))}
                              </CandidateListScroll>

                              {sortedListCandidates.length > 0 && (
                                <ListPaginationBar>
                                  <ListPaginationInfo>
                                    {(listPageSafe - 1) * LIST_PAGE_SIZE + 1}
                                    {"–"}
                                    {Math.min(listPageSafe * LIST_PAGE_SIZE, sortedListCandidates.length)} {t("of")} {sortedListCandidates.length}
                                  </ListPaginationInfo>
                                  <ListPaginationButtons>
                                    <ListPageArrow
                                      type="button"
                                      disabled={listPageSafe === 1}
                                      onClick={() => setListPage(listPageSafe - 1)}
                                    >
                                      <ChevronLeftIcon />
                                    </ListPageArrow>
                                    {listPageWindow.map((p, i) =>
                                      p === "…" ? (
                                        <ListPageEllipsis key={`ellipsis-${i}`}>…</ListPageEllipsis>
                                      ) : (
                                        <ListPageNumber
                                          key={p}
                                          type="button"
                                          $active={p === listPageSafe}
                                          onClick={() => setListPage(p)}
                                        >
                                          {p}
                                        </ListPageNumber>
                                      )
                                    )}
                                    <ListPageArrow
                                      type="button"
                                      disabled={listPageSafe === listTotalPages}
                                      onClick={() => setListPage(listPageSafe + 1)}
                                    >
                                      <ChevronRightIcon />
                                    </ListPageArrow>
                                  </ListPaginationButtons>
                                </ListPaginationBar>
                              )}
                            </CandidateListCol>

                            <DetailCol>
                              <DetailHeaderRow>
                                <RankScore $color={scoreColor(selected.score)}>
                                  <strong>{formatScore100(selected.score)}</strong>
                                  <span>/ 100</span>
                                </RankScore>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, fontSize: 15, color: COLORS.BLACK }}>{selected.candidateName}</div>
                                  <div style={{ fontSize: 12, color: COLORS.TEXT.PRIMARY }}>{rankResult.jobTitle}</div>
                                </div>
                                <RankPill>{t("Rank")} #{selectedRank}</RankPill>
                              </DetailHeaderRow>

                              <DetailMetaRow>
                                {location && <span><PlaceOutlinedIcon />{location}</span>}
                                {typeof selected.yearsExperience === "number" && (
                                  <span><EventOutlinedIcon />{selected.yearsExperience} {t("years")}</span>
                                )}
                              </DetailMetaRow>

                              <div style={{ fontSize: 13, lineHeight: 1.6, color: COLORS.TEXT.PRIMARY, marginBottom: 10 }}>
                                {selected.summary}
                              </div>

                              <RowActions style={{ marginBottom: 14 }}>
                                {(selected.strengths?.length > 0 || selected.gaps?.length > 0) && (
                                  <ExpandToggle
                                    type="button"
                                    $expanded={isExpanded}
                                    onClick={() => setExpandedCandidateId(isExpanded ? null : selected.candidateId)}
                                  >
                                    {isExpanded ? t("Hide details") : t("Show details")}
                                    <ExpandMoreIcon />
                                  </ExpandToggle>
                                )}
                                <ExpandToggle type="button" onClick={() => openEmailComposer(selected)}>
                                  <MailOutlineIcon />
                                  {t("Draft email")}
                                </ExpandToggle>
                              </RowActions>

                              {isExpanded && (
                                <>
                                  {matchBars.length > 0 && (
                                    <MatchBarGrid>
                                      {matchBars.map((m) => (
                                        <MatchBarTile key={m.label}>
                                          <MatchBarLabel>{m.label}</MatchBarLabel>
                                          <MatchBarValue>{m.value}%</MatchBarValue>
                                          <MatchBarTrack>
                                            <MatchBarFill $pct={m.value} $color={matchBarColor(m.value)} />
                                          </MatchBarTrack>
                                        </MatchBarTile>
                                      ))}
                                    </MatchBarGrid>
                                  )}

                                  {selected.strengths?.length > 0 && (
                                    <DetailBlock $tone="success">
                                      <DetailBlockTitle $tone="success">
                                        <CheckCircleOutlineIcon />
                                        {t("Strengths")}
                                      </DetailBlockTitle>
                                      <ul>
                                        {selected.strengths.map((s, i) => <li key={i}>{s}</li>)}
                                      </ul>
                                    </DetailBlock>
                                  )}
                                  {selected.gaps?.length > 0 && (
                                    <DetailBlock $tone="warning">
                                      <DetailBlockTitle $tone="warning">
                                        <WarningAmberOutlinedIcon />
                                        {t("Gaps")}
                                      </DetailBlockTitle>
                                      <ul>
                                        {selected.gaps.map((g, i) => <li key={i}>{g}</li>)}
                                      </ul>
                                    </DetailBlock>
                                  )}
                                  {selected.summary && (
                                    <InsightNoteCard>
                                      <LightbulbOutlinedIcon style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }} />
                                      <div>
                                        <div style={{ fontWeight: 600, marginBottom: 3 }}>{t("Why this candidate ranked")} #{selectedRank}</div>
                                        {selected.summary}
                                      </div>
                                    </InsightNoteCard>
                                  )}
                                </>
                              )}
                            </DetailCol>
                          </RankingSplit>
                        );
                      })()}
                    </>
                  ),
                },
                !emailOnlyView && rankShowInsights && {
                  key: "insights",
                  label: t("Insights"),
                  children: (
                    <>
                      <InsightsHeaderRow>
                        <InsightsIconChip $bg="#E9EDFC" $color={COLORS.PRIMARY}><LightbulbOutlinedIcon /></InsightsIconChip>
                        <div>
                          <InsightsIntroTitle>{t("Candidate Insights")}</InsightsIntroTitle>
                          <InsightsHeaderSubtitle>{t("Get a quick overview of your candidates, including skill distribution, experience trends, and overall quality.")}</InsightsHeaderSubtitle>
                        </div>
                      </InsightsHeaderRow>

                      <InsightsSection>
                        <KpiGrid>
                          <KpiTile>
                            <KpiIconWrap $bg="#F1EAFB" $color="#7B5FE0"><GroupsOutlinedIcon /></KpiIconWrap>
                            <strong>
                              {displayedCandidates.length}
                              {filterIndex > -1 && ` / ${rankResult.ranked.length}`}
                            </strong>
                            <span>{t("Total Candidates")}</span>
                          </KpiTile>
                          <KpiTile>
                            <KpiIconWrap $bg="#E7F7EE" $color={COLORS.SUCCESS}><ArrowUpwardIcon /></KpiIconWrap>
                            <strong>{insights.shortlisted}</strong>
                            <span>{t("Shortlisted")}</span>
                          </KpiTile>
                          <KpiTile>
                            <KpiIconWrap $bg="#FDEDED" $color={COLORS.ERROR}><CancelOutlinedIcon /></KpiIconWrap>
                            <strong>{insights.notShortlisted}</strong>
                            <span>{t("Not Shortlisted")}</span>
                          </KpiTile>
                          <KpiTile>
                            <KpiIconWrap $bg="#F1EAFB" $color="#7B5FE0"><StarOutlineIcon /></KpiIconWrap>
                            <strong>{insights.avgScore !== null ? formatScore100(insights.avgScore) : "—"}</strong>
                            <span>{t("Avg. Score")}</span>
                          </KpiTile>
                        </KpiGrid>
                      </InsightsSection>

                      {/* Row: skill/score breakdowns — both bar-based, same visual weight */}
                      <InsightsTwoCol>
                        {insights.topSkills.length > 0 && (() => {
                          const totalRanked = displayedCandidates.length || 1;
                          return (
                            <InsightsSection>
                              <InsightsHeaderRow>
                                <InsightsIconChip $bg="#E9EDFC" $color={COLORS.PRIMARY}><CodeOutlinedIcon /></InsightsIconChip>
                                <div>
                                  <InsightsHeaderTitle>{t("Skill Analysis")}</InsightsHeaderTitle>
                                  <InsightsHeaderSubtitle>{t("Top skills across all candidates")}</InsightsHeaderSubtitle>
                                </div>
                              </InsightsHeaderRow>
                              {insights.topSkills.map(({ skill, count }, idx) => {
                                const pct = Math.round((count / totalRanked) * 100);
                                return (
                                  <SkillBarRow key={skill}>
                                    <SkillBarLabel title={skill}>{skill}</SkillBarLabel>
                                    <SkillBarTrack><SkillBarFill $pct={pct} $color={CHART_PALETTE[idx % CHART_PALETTE.length]} /></SkillBarTrack>
                                    <SkillBarPct>{pct}%</SkillBarPct>
                                  </SkillBarRow>
                                );
                              })}
                            </InsightsSection>
                          );
                        })()}

                        {(insights.matchAverages.skillMatch !== null || insights.matchAverages.experienceMatch !== null) && (
                          <InsightsSection>
                            <InsightsHeaderRow>
                              <InsightsIconChip $bg="#E9EDFC" $color={COLORS.PRIMARY}><TrackChangesOutlinedIcon /></InsightsIconChip>
                              <div>
                                <InsightsHeaderTitle>{t("Match Distribution")}</InsightsHeaderTitle>
                                <InsightsHeaderSubtitle>{t("How well candidates match your requirements")}</InsightsHeaderSubtitle>
                              </div>
                            </InsightsHeaderRow>
                            <MatchDistributionChart matchAverages={insights.matchAverages} />
                          </InsightsSection>
                        )}
                      </InsightsTwoCol>

                      {/* Row: the two donuts — matching visual shape and size */}
                      <InsightsTwoCol>
                        {insights.experienceDistribution.some((b) => b.count > 0) && (() => {
                          const expWithCounts = insights.experienceDistribution.filter((b) => b.count > 0);
                          const expTotal = expWithCounts.reduce((sum, b) => sum + b.count, 0);
                          return (
                            <InsightsSection>
                              <InsightsHeaderRow>
                                <InsightsIconChip $bg="#E9EDFC" $color={COLORS.PRIMARY}><EventOutlinedIcon /></InsightsIconChip>
                                <div>
                                  <InsightsHeaderTitle>{t("Experience Distribution")}</InsightsHeaderTitle>
                                  <InsightsHeaderSubtitle>{t("Years of relevant experience")}</InsightsHeaderSubtitle>
                                </div>
                              </InsightsHeaderRow>
                              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                                <ExperienceDistributionChart experienceDistribution={insights.experienceDistribution} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {expWithCounts.map((b, idx) => (
                                    <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: COLORS.TEXT.PRIMARY, marginBottom: 6 }}>
                                      <span style={{
                                        display: "inline-block", width: 9, height: 9, borderRadius: 2,
                                        background: EXPERIENCE_DONUT_SHADES[idx % EXPERIENCE_DONUT_SHADES.length], flexShrink: 0,
                                      }} />
                                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.label} yrs</span>
                                      <span style={{ fontWeight: 500, flexShrink: 0 }}>{Math.round((b.count / expTotal) * 100)}% ({b.count})</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </InsightsSection>
                          );
                        })()}

                        {insights.topLocations.length > 0 && (
                          <InsightsSection>
                            <InsightsHeaderRow>
                              <InsightsIconChip $bg="#F1EAFB" $color="#7B5FE0"><PlaceOutlinedIcon /></InsightsIconChip>
                              <div>
                                <InsightsHeaderTitle>{t("Location Analysis")}</InsightsHeaderTitle>
                                <InsightsHeaderSubtitle>{t("Candidate locations")}</InsightsHeaderSubtitle>
                              </div>
                            </InsightsHeaderRow>
                            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                              <LocationDistributionChart topLocations={insights.topLocations} locatedCount={insights.locatedCount} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {insights.topLocations.map(({ label, count }, idx) => (
                                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: COLORS.TEXT.PRIMARY, marginBottom: 6 }}>
                                    <span style={{
                                      display: "inline-block", width: 9, height: 9, borderRadius: 2,
                                      background: CHART_PALETTE[idx % CHART_PALETTE.length], flexShrink: 0,
                                    }} />
                                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={label}>{label}</span>
                                    <span style={{ fontWeight: 500, flexShrink: 0 }}>{Math.round((count / insights.locatedCount) * 100)}% ({count})</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </InsightsSection>
                        )}
                      </InsightsTwoCol>

                      {/* Row: pill-tag summaries */}
                      <InsightsTwoCol>
                        {insights.topSkills.length > 0 && (
                          <InsightsSection>
                            <InsightsHeaderRow>
                              <InsightsIconChip $bg="#F1EAFB" $color="#7B5FE0"><StarOutlineIcon /></InsightsIconChip>
                              <div>
                                <InsightsHeaderTitle>{t("Top Matching Skills")}</InsightsHeaderTitle>
                                <InsightsHeaderSubtitle>{t("Most common skills in top candidates")}</InsightsHeaderSubtitle>
                              </div>
                            </InsightsHeaderRow>
                            <SkillTagRow>
                              {insights.topSkills.map(({ skill }) => <SkillTag key={skill}>{skill}</SkillTag>)}
                            </SkillTagRow>
                          </InsightsSection>
                        )}

                        {insights.commonGaps.length > 0 && (
                        <InsightsSection>
                          <InsightsHeaderRow>
                            <InsightsIconChip $bg="#FFF3DC" $color="#8A5A00"><WarningAmberOutlinedIcon /></InsightsIconChip>
                            <div>
                              <InsightsHeaderTitle>{t("Common Skill Gaps")}</InsightsHeaderTitle>
                              <InsightsHeaderSubtitle>{t("Skills frequently missing in candidates")}</InsightsHeaderSubtitle>
                            </div>
                          </InsightsHeaderRow>
                          <SkillTagRow>
                            {insights.commonGaps.map((gap) => <SkillTag key={gap} $tone="warning" title={gap}>{gap}</SkillTag>)}
                          </SkillTagRow>
                        </InsightsSection>
                        )}
                      </InsightsTwoCol>

                      {insights.qualityTrend.length > 1 && (
                        <InsightsSection>
                          <InsightsHeaderRow>
                            <InsightsIconChip $bg="#E7F7EE" $color={COLORS.SUCCESS}><TrendingUpOutlinedIcon /></InsightsIconChip>
                            <div>
                              <InsightsHeaderTitle>{t("Candidate Quality Trends")}</InsightsHeaderTitle>
                              <InsightsHeaderSubtitle>{t("Overall quality based on ranking scores")}</InsightsHeaderSubtitle>
                            </div>
                          </InsightsHeaderRow>
                          <InsightsTwoCol>
                            <QualityTrendChart qualityTrend={insights.qualityTrend} />
                            <InsightNoteCard>
                              <LightbulbOutlinedIcon style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }} />
                              <div>{t("Higher-ranked candidates show stronger skill match and relevant experience compared to lower-ranked candidates.")}</div>
                            </InsightNoteCard>
                          </InsightsTwoCol>
                        </InsightsSection>
                      )}

                      {insights.topCandidates.length > 0 && (
                        <InsightsSection>
                          <InsightsSectionTitle>{t("Top 10 candidates")}</InsightsSectionTitle>
                          {insights.topCandidates.map((candidate, idx) => (
                            <TopCandidateRow key={candidate.candidateId}>
                              <TopCandidateRank>{idx + 1}</TopCandidateRank>
                              <TopCandidateName title={candidate.candidateName}>{candidate.candidateName}</TopCandidateName>
                              <TopCandidateScore $color={scoreColor(candidate.score)}>
                                {formatScore100(candidate.score)} / 100
                              </TopCandidateScore>
                            </TopCandidateRow>
                          ))}
                        </InsightsSection>
                      )}
                    </>
                  ),
                },
              ].filter(Boolean)}
            />
          );

          const emailReviewContent = emailReport && emailReport.drafts.length > 0 && (() => {
            const idx = Math.min(bulkReviewIndex, emailReport.drafts.length - 1);
            const draft = emailReport.drafts[idx];
            const initials = draft.candidateName?.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";
            return (
              <EmailReviewShell>
                <EmailReviewHeader>
                  <EmailReviewHeaderLeft>
                    <EmailReviewHeaderIcon><MailOutlineIcon /></EmailReviewHeaderIcon>
                    <div>
                      <EmailReviewHeaderTitle>{t("Review Emails")}</EmailReviewHeaderTitle>
                      <EmailReviewHeaderSubtitle>{t("Preview and send the email to each candidate.")}</EmailReviewHeaderSubtitle>
                    </div>
                  </EmailReviewHeaderLeft>
                  <EmailReviewCloseButton type="button" onClick={() => setEmailReviewModalOpen(false)}>
                    <CloseOutlinedIcon />
                  </EmailReviewCloseButton>
                </EmailReviewHeader>

                <EmailReviewNavBar>
                  <EmailReviewNavBtn
                    type="button"
                    disabled={idx === 0}
                    onClick={() => setBulkReviewIndex((i) => Math.max(0, i - 1))}
                  >
                    <ChevronLeftIcon /> {t("Previous")}
                  </EmailReviewNavBtn>

                  <EmailReviewCandidate>
                    <EmailReviewAvatar>{initials}</EmailReviewAvatar>
                    <EmailReviewCandidateText>
                      <EmailReviewCandidateName title={draft.candidateName}>{draft.candidateName}</EmailReviewCandidateName>
                      <EmailReviewCandidateMeta>{t("Candidate {{n}} of {{total}}", { n: idx + 1, total: emailReport.drafts.length })}</EmailReviewCandidateMeta>
                    </EmailReviewCandidateText>
                    {draft.sent ? (
                      <EmailReviewStatusPill $tone="sent"><CheckCircleOutlineIcon /> {t("Sent")}</EmailReviewStatusPill>
                    ) : draft.loading ? (
                      <EmailReviewStatusPill $tone="sending">{t("Drafting…")}</EmailReviewStatusPill>
                    ) : draft.generated ? (
                      <EmailReviewStatusPill $tone="ready"><CheckCircleOutlineIcon /> {t("Ready to send")}</EmailReviewStatusPill>
                    ) : null}
                  </EmailReviewCandidate>

                  <EmailReviewNavBtn
                    type="button"
                    disabled={idx === emailReport.drafts.length - 1}
                    onClick={() => setBulkReviewIndex((i) => Math.min(emailReport.drafts.length - 1, i + 1))}
                  >
                    {t("Next")} <ChevronRightIcon />
                  </EmailReviewNavBtn>
                </EmailReviewNavBar>

                <EmailReviewScroll>
                  {draft.error && <EmailDraftError>{draft.error}</EmailDraftError>}
                  {draft.generated && (
                    <>
                      <EmailReviewRow>
                        <EmailReviewRowIcon><PersonOutlineOutlinedIcon /></EmailReviewRowIcon>
                        <EmailReviewRowLabel>{t("To")}</EmailReviewRowLabel>
                        <EmailReviewRowInput
                          value={draft.recipient}
                          disabled={draft.sent}
                          onChange={(e) => updateEmailDraft(emailReport.reportId, draft.candidateId, { recipient: e.target.value })}
                          placeholder={t("candidate@example.com")}
                        />
                      </EmailReviewRow>
                      <EmailReviewRow>
                        <EmailReviewRowIcon><DescriptionOutlinedIcon /></EmailReviewRowIcon>
                        <EmailReviewRowLabel>{t("Subject")}</EmailReviewRowLabel>
                        <EmailReviewRowInput
                          value={draft.subject}
                          disabled={draft.sent}
                          onChange={(e) => updateEmailDraft(emailReport.reportId, draft.candidateId, { subject: e.target.value })}
                        />
                      </EmailReviewRow>
                      <EmailReviewBodyBox
                        value={draft.body}
                        disabled={draft.sent}
                        onChange={(e) => updateEmailDraft(emailReport.reportId, draft.candidateId, { body: e.target.value })}
                        autoSize={{ minRows: 10, maxRows: 10 }}
                      />
                    </>
                  )}
                </EmailReviewScroll>

                <EmailReviewFooter>
                  <EmailReviewCancelButton type="button" onClick={() => setEmailReviewModalOpen(false)}>
                    {t("Cancel")}
                  </EmailReviewCancelButton>
                  <EmailSendButton
                    type="button"
                    disabled={draft.sent || !draft.generated}
                    loading={draft.sending}
                    onClick={() => handleSendEmail(emailReport.reportId, draft.candidateId)}
                  >
                    {draft.sent ? (
                      <><CheckCircleOutlineIcon /> {t("Sent")}</>
                    ) : (
                      <><SendOutlinedIcon /> {t("Send Email")}</>
                    )}
                  </EmailSendButton>
                </EmailReviewFooter>
              </EmailReviewShell>
            );
          })();
          return emailOnlyView ? (
            <>
              {!emailReviewModalOpen && (
                <ReviewEmailsRow>
                  <ReviewEmailsButton onClick={() => setEmailReviewModalOpen(true)}>
                    <MailOutlineIcon />
                    {t("Review Emails")}
                    <NorthEastIcon style={{ fontSize: 14 }} />
                  </ReviewEmailsButton>
                </ReviewEmailsRow>
              )}
              <Modal
                open={emailReviewModalOpen}
                onCancel={() => setEmailReviewModalOpen(false)}
                footer={null}
                closable={false}
                width={640}
                wrapClassName="email-review-modal"
                bodyStyle={{ padding: 0 }}
                destroyOnClose
              >
                {emailReviewContent}
              </Modal>
            </>
          ) : tabsNode;
            })()}
            </div>
          </div>
          <div
            style={{
              flexShrink: 0,
              alignSelf: chatCollapsed ? "center" : "stretch",
              maxHeight: "94vh",
              border: chatCollapsed ? "none" : "1px solid #ECEFF7",
              borderRadius: chatCollapsed ? 0 : 14,
              overflow: chatCollapsed ? "visible" : "hidden",
            }}
          >
            <RecruitmentChatPanel
              collapsed={chatCollapsed}
              onToggleCollapse={() => setChatCollapsed((c) => !c)}
              maxHeight="94vh"
              active={Boolean(rankResult)}
              jobId={rankJobId}
              jobTitle={rankResult?.jobTitle}
              jobs={(data || [])
                .filter((j) => filter(candidateList, (c) => c?.jobId?.toString() === j?.jobId?.toString()).length > 0)
                .map((j) => ({ jobId: j.jobId, title: j.title }))}
              onSelectJob={handleChatSelectJob}
              candidates={displayedCandidates}
              selectedCandidateId={selectedCandidateId}
              activeSection={rankModalTab}
              emailReportSummary={activeEmailReportForChat}
              filterDescription={filterIndex > -1 ? filterHistory[filterIndex]?.description : null}
              requestBulkEmail={requestBulkEmailFromChat}
              onSendAllEmails={sendAllEmails}
              onOpenEmailReport={openEmailReport}
              onViewCandidate={handleViewCandidate}
              onShowProgress={handleShowProgress}
              onCandidateUpdated={getData}
              onJobUpdated={getData}
              onActionProgressChange={handleActionProgressChange}
              onProgressCardsChange={setProgressCardsById}
              bulkProgress={bulkAnalysisCard}
            />
          </div>
        </div>
      </Modal>
      <Modal
        title={
          emailCandidate ? (
            <AiTitleWrap>
              <AiBadge>✦</AiBadge>
              {t("Draft email")} — {emailCandidate.candidateName}
            </AiTitleWrap>
          ) : (
            ""
          )
        }
        open={Boolean(emailCandidate)}
        onCancel={closeEmailComposer}
        footer={null}
        width={560}
      >
        {emailCandidate && (
          <>
            <EmailFieldRow>
              <EmailFieldLabel>{t("Purpose")}</EmailFieldLabel>
              <Select
                value={emailPurpose}
                onChange={setEmailPurpose}
                options={EMAIL_PURPOSE_OPTIONS}
                style={{ flex: 1 }}
              />
            </EmailFieldRow>
            <EmailFieldRow>
              <EmailFieldLabel>{t("Tone")}</EmailFieldLabel>
              <Select
                value={emailTone}
                onChange={setEmailTone}
                options={EMAIL_TONE_OPTIONS}
                style={{ flex: 1 }}
              />
            </EmailFieldRow>

            <RankAiButton
              type="button"
              loading={emailLoading}
              onClick={handleGenerateEmail}
              style={{ marginTop: 4, marginBottom: 4 }}
            >
              {`✦ ${emailGenerated ? t("Regenerate") : t("Generate email")}`}
            </RankAiButton>

            {emailError && <ChatErrorText>{emailError}</ChatErrorText>}

            {emailGenerated && (
              <EmailPreview>
                <EmailFieldRow>
                  <EmailFieldLabel>{t("Recipient")}</EmailFieldLabel>
                  <CommandInput
                    value={emailRecipient}
                    onChange={(e) => setEmailRecipient(e.target.value)}
                    placeholder={t("candidate@example.com")}
                  />
                </EmailFieldRow>
                <EmailFieldRow>
                  <EmailFieldLabel>{t("Subject")}</EmailFieldLabel>
                  <CommandInput
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                  />
                </EmailFieldRow>
                <EmailFieldLabel style={{ marginBottom: 6 }}>{t("Body")}</EmailFieldLabel>
                <TextArea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  autoSize={{ minRows: 8, maxRows: 16 }}
                />
                <EmailPreviewNote>
                  {t("This is a preview — nothing is sent from here. Sending will be added separately.")}
                </EmailPreviewNote>
              </EmailPreview>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}

export default JobsList;
